import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  onSnapshot,
  query,
  orderBy,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, app } from "../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { addJobTotal } from "../utils/addJobTotal";
import ClockPanel from "../clock/ClockPanel";
import ClockCalendar from "../clock/ClockCalendar";
import { useClockInLogic } from "../clock/useClockInLogic";
import { useJobVisits } from "../clock/jobVisits";
import { JobCheckInButton, ActiveJobBanner, JobVisitHistory } from "../clock/JobCheckIn";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
import ClockTime from "./ClockTime";
import EmbeddedCalendar from "../calendar/EmbeddedCalendar";
import CustomerCodes from "./CustomerCodes";
import Maps from "./Maps";
import TimeOff from "./TimeOff";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import { materialsNeededForWeeks } from "../utils/materialsForWeek";
import * as XLSX from "xlsx";
import "./JobDetailsShared.css";
import TeamMessaging from "./TeamMessaging";
import RoughEstimateBuilder from "./RoughEstimateBuilder";
import { applyOccurrenceOverrideToRenderedJob, getOccurrenceOverrideRenderJobs, mergeRenderedRecurringJobs, saveRecurringJobEdit, closeOrDeleteJob } from "../utils/recurringEditUtils";
import {
  refreshKeepingView,
  restoreScrollAfterRefresh,
  getCompletionOccurrenceKey,
  occurrenceIsInDateList,
  makeOccurrenceDateMatcher,
} from "../utils/recurringOccurrenceUtils";
import QuickJobFieldEditor from "../components/QuickJobFieldEditor";
import CollapsibleActionSection from "../components/CollapsibleActionSection";
import JobHistoryPopup from "../components/JobHistoryPopup";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import {
  getMaterialsGroupId,
  fetchAllJobMaterials,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";


const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes jmc-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "jmc-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

const serviceTypes = [
  "Proposal",
  "Meeting",
  "Construction",
  "Maintenance",
  "Action Items",
  "Cleaning",
  "Bulk Trash",
  "Inspection",
  "Electrical",
  "Plumbing",
  "HVAC",
  "Concrete",
];

const statusOptions = ["Active", "Completed", "Invoice Sent", "On Hold"];

const timeWindowOptions = [
  "ANY",
  "8AM - 10AM",
  "9AM - 11AM",
  "10AM - 12PM",
  "11AM - 1PM",
  "12PM - 2PM",
  "1PM - 3PM",
  "2PM - 4PM",
  "3PM - 5PM",
  "4PM - 6PM",
  "Custom Time"
];

// ✅ Always treat startDate as a DATE-ONLY in local time (no timezone shifting)
// ✅ Canonical date key (YYYY-MM-DD) in LOCAL time, no timezone shifting
const toDateKey = (v) => {
  if (!v) return "";

  // Firestore Timestamp
  if (typeof v === "object" && v?.seconds) {
    return moment(v.seconds * 1000).format("YYYY-MM-DD");
  }

  // strings: keep only YYYY-MM-DD
  if (typeof v === "string") {
    const s = v.slice(0, 10);
    return moment(s, "YYYY-MM-DD", true).isValid() ? s : "";
  }

  // Date
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const todayKey = () => moment().format("YYYY-MM-DD");
const tomorrowKey = () => moment().add(1, "day").format("YYYY-MM-DD");
const yesterdayKey = () => moment().subtract(1, "day").format("YYYY-MM-DD");
const endOfWeekKey = () => moment().endOf("week").format("YYYY-MM-DD");
const startOfNextWeekKey = () =>
  moment().add(1, "week").startOf("week").format("YYYY-MM-DD");
const endOfNextWeekKey = () =>
  moment().add(1, "week").endOf("week").format("YYYY-MM-DD");


const getRecurrenceType = (job) => {
  let type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence ??
        job?.reccurance ??
        job?.recurring ??
        job?.repeat ??
        "none";

  type = String(type || "none").toLowerCase().trim();

  if (!type || type === "one-time" || type === "onetime") return "none";
  if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
  if (type === "customdates" || type === "custom dates") return "customDates";

  return type;
};

const isRecurringJob = (job) => getRecurrenceType(job) !== "none";

// This identifies WHICH OCCURRENCE a job object represents, so it must stay
// anchored to occurrenceDate/startDate, not __renderDate. __renderDate can
// differ from occurrenceDate for the same occurrence (e.g. a spillover
// render day), and this key is used both to WRITE completedDates and to
// check isOccurrenceCompleted - if it drifted to __renderDate, completing
// a job could write a different date than the one being checked, leaving
// the job stuck as "not completed" even after being marked done.
const getOccurrenceDateKey = (job) =>
  toDateKey(job.occurrenceDate || job.startDate);

// Keep one rendered tile/result for each recurring WO occurrence.
// This prevents the same series occurrence from appearing more than once
// when it is returned by both the normal recurrence and override paths.
const dedupeRenderedJobs = (renderedJobs = []) => {
  const seen = new Map();

  renderedJobs.forEach((job) => {
    const occurrenceKey = getOccurrenceDateKey(job) || "UNSCHEDULED";
    const recurringKey = isRecurringJob(job)
      ? String(job.wo || job.id || "job").trim().toLowerCase()
      : String(job.id || job.wo || "job").trim().toLowerCase();
    const renderKey = toDateKey(job.__renderDate || occurrenceKey) || occurrenceKey;
    const key = `${recurringKey}_${occurrenceKey}_${renderKey}`;

    if (!seen.has(key)) seen.set(key, job);
  });

  return Array.from(seen.values());
};

const normalizeWeekday = (value) => {
  if (typeof value === "number" && value >= 0 && value <= 6) return value;

  const raw = String(value ?? "").trim().toLowerCase();
  if (/^[0-6]$/.test(raw)) return Number(raw);

  const names = {
    sunday: 0,
    sun: 0,
    monday: 1,
    mon: 1,
    tuesday: 2,
    tue: 2,
    tues: 2,
    wednesday: 3,
    wed: 3,
    thursday: 4,
    thu: 4,
    thur: 4,
    thurs: 4,
    friday: 5,
    fri: 5,
    saturday: 6,
    sat: 6,
  };

  return Object.prototype.hasOwnProperty.call(names, raw) ? names[raw] : null;
};

const getRecurringDaysOfWeek = (job, seriesStart) => {
  const recurrence =
    typeof job?.recurrence === "object" ? job.recurrence : {};

  const rawDays =
    recurrence.daysOfWeek ||
    recurrence.weekDays ||
    recurrence.weekdays ||
    job.daysOfWeek ||
    job.weekDays ||
    job.weekdays ||
    [];

  const normalized = (Array.isArray(rawDays) ? rawDays : [rawDays])
    .map(normalizeWeekday)
    .filter((day) => day !== null);

  if (normalized.length) return Array.from(new Set(normalized));

  return [seriesStart.day()];
};

const getHomeOccurrencesForJob = (job, rangeStart, rangeEnd) => {
  const recurrenceType = getRecurrenceType(job);
  const recurrence =
    typeof job?.recurrence === "object" ? job.recurrence : {};

  const seriesStart = moment(
    toDateKey(job.startDate),
    "YYYY-MM-DD",
    true
  ).startOf("day");

  if (!seriesStart.isValid()) return [];

  const windowStart = moment(rangeStart).startOf("day");
  const requestedEnd = moment(rangeEnd).startOf("day");
  const recurrenceEnd = toDateKey(
    recurrence.endsOn || recurrence.endDate || job.recurrenceEndDate
  );

  const hardEnd = recurrenceEnd
    ? moment.min(
        requestedEnd,
        moment(recurrenceEnd, "YYYY-MM-DD", true).startOf("day")
      )
    : requestedEnd;

  if (hardEnd.isBefore(seriesStart, "day")) return [];

  const dates = new Set();
  const addDate = (date) => {
    if (!date.isValid()) return;
    if (date.isBefore(seriesStart, "day")) return;
    if (date.isBefore(windowStart, "day")) return;
    if (date.isAfter(hardEnd, "day")) return;
    dates.add(date.format("YYYY-MM-DD"));
  };

  if (recurrenceType === "customDates") {
    const customDates =
      recurrence.customDates ||
      job.customDates ||
      [];

    (Array.isArray(customDates) ? customDates : [customDates]).forEach((value) => {
      const key = toDateKey(value);
      if (key) addDate(moment(key, "YYYY-MM-DD", true));
    });

    return Array.from(dates).sort();
  }

  if (recurrenceType === "daily") {
    let cursor = moment.max(seriesStart, windowStart).clone();

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      addDate(cursor);
      cursor.add(1, "day");
    }

    return Array.from(dates).sort();
  }

  if (recurrenceType === "weekly" || recurrenceType === "biweekly") {
    const selectedDays = getRecurringDaysOfWeek(job, seriesStart);
    const intervalWeeks = recurrenceType === "biweekly" ? 2 : 1;
    let cursor = moment.max(seriesStart, windowStart).clone();

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      const weeksFromStart = cursor
        .clone()
        .startOf("week")
        .diff(seriesStart.clone().startOf("week"), "weeks");

      if (
        weeksFromStart >= 0 &&
        weeksFromStart % intervalWeeks === 0 &&
        selectedDays.includes(cursor.day())
      ) {
        addDate(cursor);
      }

      cursor.add(1, "day");
    }

    return Array.from(dates).sort();
  }

  const monthIntervals = {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    biyearly: 6,
    yearly: 12,
  };

  if (monthIntervals[recurrenceType]) {
    let cursor = seriesStart.clone();
    while (cursor.isBefore(windowStart, "day")) {
      cursor.add(monthIntervals[recurrenceType], "months");
    }

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      addDate(cursor);
      cursor.add(monthIntervals[recurrenceType], "months");
    }

    return Array.from(dates).sort();
  }

  // Safe fallback for an unknown recurring type.
  addDate(seriesStart);
  return Array.from(dates).sort();
};

const expandJobsForHome = (jobs) => {
  const outMap = new Map(); // jobId + date → job

  jobs.forEach((job) => {
  
    // ---------- NON-RECURRING ----------
if (!isRecurringJob(job)) {
  // ✅ hide Completed one-time jobs from Home list entirely
  if (job.status === "Completed" || job.status === "Invoice Sent") return;

  const k = toDateKey(job.startDate);

  // ✅ NEW: include unscheduled jobs so "Needs Scheduling" can show them
  if (!k) {
    const key = `${job.id}_UNSCHEDULED`;
    if (!outMap.has(key)) {
      outMap.set(key, {
        ...job,
        startDate: "",        // keep blank so toDateKey() stays ""
        occurrenceDate: "",   // also blank
      });
    }
    return;
  }

  const key = `${job.id}_${k}`;
  if (!outMap.has(key)) {
    outMap.set(key, {
      ...job,
      startDate: k,
      occurrenceDate: k,
    });
  }
  return;
}

   // ---------- RECURRING ----------
// NOTE: recurring series are intentionally NOT hidden based on job.status
// here. That field lives on the shared master doc, and invoicing a single
// occurrence (see SendInvoices.js handleInvoiceSent) sets it to
// "Invoice Sent" for the whole series — which would wipe every future
// occurrence off Home. Calendar/EmbeddedCalendar don't gate recurring
// visibility on this field either; they rely on per-occurrence
// completedDates/invoicedDates instead, which is what should decide
// whether a specific occurrence is closed.

const startKey = toDateKey(job.startDate);

// ✅ NEW: include recurring jobs with missing startDate so they can be scheduled
if (!startKey) {
  const key = `${job.id}_UNSCHEDULED`;
  if (!outMap.has(key)) {
    outMap.set(key, {
      ...job,
      startDate: "",
      occurrenceDate: "",
    });
  }
  return;
}


    const seriesStart = moment(startKey, "YYYY-MM-DD");
    const end = moment(endOfNextWeekKey(), "YYYY-MM-DD");

    // Generate through the current week, but do not show every historical
    // recurring occurrence as overdue. We keep only the latest open past
    // occurrence plus every open occurrence from today through week-end.
    const rawDates = getHomeOccurrencesForJob(job, seriesStart, end) || [];

    const uniq = new Set(rawDates.map((d) => toDateKey(d)).filter(Boolean));

    // `k` below is the CANONICAL date the recurrence rule produced. If the
    // user rescheduled just this one occurrence, its completion can be
    // recorded under either that canonical date or the day it moved to, so
    // these matchers check both. A plain Set on the raw date missed the
    // rescheduled case entirely, which is why a completed-but-moved
    // occurrence never dropped off Home.
    const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates);
    const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates);

    const openDates = Array.from(uniq)
      .filter((k) => !isExcluded(k) && !isCompleted(k))
      .sort();

    const today = todayKey();
    const latestPastDate =
      openDates.filter((k) => k < today).slice(-1)[0] || "";

    openDates.forEach((k) => {
      // Recurring series should contribute only its latest missed occurrence
      // to Overdue. Current and upcoming occurrences remain available for
      // Due Today, Due Tomorrow, and Due This Week.
      if (k < today && k !== latestPastDate) return;

      const key = `${job.id}_${k}`;
      if (!outMap.has(key)) {
        outMap.set(key, {
          ...job,
          startDate: k,
          endDate: k,
          occurrenceDate: k,
          originalOccurrenceDate: k,
        });
      }
    });
  });

  return Array.from(outMap.values());
};


const getDurationMismatch = (job) => {
  if (!job.startDate || !job.endDate || !job.jobPlan?.estimatedDays) return null;

  const scheduledDays =
    moment(job.endDate).diff(moment(job.startDate), "days") + 1;

  if (scheduledDays !== job.jobPlan.estimatedDays) {
    return {
      scheduledDays,
      estimatedDays: job.jobPlan.estimatedDays,
    };
  }

  return null;
};

const expandJobsForCalendar = (jobs) => {
  const outMap = new Map();
  const calendarEnd = moment().add(12, "months").endOf("day");

  jobs.forEach((job) => {
    // ---------- NON-RECURRING ----------
    if (!isRecurringJob(job)) {
      const k = toDateKey(job.startDate);
      if (!k) return;

      const key = `${job.id}_${k}`;
      outMap.set(key, {
        ...job,
        startDate: k,
        occurrenceDate: k,
      });
      return;
    }

    // ---------- RECURRING ----------
    const startKey = toDateKey(job.startDate);
    if (!startKey) return;

    const start = moment(startKey, "YYYY-MM-DD");
    const rawDates = getOccurrencesForJob(job, start, calendarEnd) || [];

    const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates);
    const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates);

    rawDates.forEach((d) => {
      const k = toDateKey(d);
      if (!k) return;

      // still hide only cancelled/excluded occurrences
      if (isExcluded(k)) return;

      const key = `${job.id}_${k}`;
      outMap.set(key, {
        ...job,
        startDate: k,
        endDate: k,
        occurrenceDate: k,
        originalOccurrenceDate: k,

        // mark this rendered occurrence as completed if its date is in
        // completedDates — under either its canonical or its rescheduled key
        occurrenceStatus: isCompleted(k) ? "Completed" : job.status,
      });
    });
  });

  return Array.from(outMap.values());
};

const parseFormattedJobDescription = (description) => {
  if (!description) return [];

  return description
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line, index, arr) => {
      // keep intentional blank lines if surrounded by content
      if (line !== "") return true;
      return arr[index - 1] !== "" || arr[index + 1] !== "";
    })
    .map((line, index) => {
      if (line.trim() === "") {
        return <div key={index} style={{ height: "10px" }} />;
      }

      if (line.startsWith("((b))")) {
        return (
          <div key={index} style={{ fontWeight: 700, marginBottom: 6 }}>
            {line.replace("((b))", "").trim()}
          </div>
        );
      }

      if (line.startsWith("((i))")) {
        return (
          <div key={index} style={{ fontStyle: "italic", marginBottom: 6 }}>
            {line.replace("((i))", "").trim()}
          </div>
        );
      }

      if (line.startsWith("((-))")) {
        return (
          <div key={index} style={{ marginLeft: 16, marginBottom: 6 }}>
            • {line.replace("((-))", "").trim()}
          </div>
        );
      }

      return (
        <div key={index} style={{ marginBottom: 6, lineHeight: 1.5 }}>
          {line}
        </div>
      );
    });
};

const getJobStartKey = (job) =>
  toDateKey(job.occurrenceDate || job.startDate);

const getJobEndKey = (job) =>
  toDateKey(job.occurrenceDate || job.endDate || job.startDate);

const jobSpansDate = (job, targetKey) => {
  const startKey = getJobStartKey(job);
  const endKey = getJobEndKey(job);

  if (!startKey) return false;
  if (!endKey) return startKey === targetKey;

  return targetKey >= startKey && targetKey <= endKey;
};

const jobSpansRange = (job, rangeStartKey, rangeEndKey) => {
  const startKey = getJobStartKey(job);
  const endKey = getJobEndKey(job);

  if (!startKey) return false;

  const safeEndKey = endKey || startKey;

  return startKey <= rangeEndKey && safeEndKey >= rangeStartKey;
};

const getTeamConversationKey = (msg) => {
  if (msg?.audience === "all") return "team_all";

  const ids = Array.from(
    new Set([msg.authorId, ...(msg.recipientIds || [])].filter(Boolean))
  ).sort();

  return `dm_${ids.join("_")}`;
};

const getTeamConversationLabel = (msg, currentUserId) => {
  if (msg?.audience === "all") return "Team All";

  const recipientNames = Array.isArray(msg?.recipientNames) ? msg.recipientNames : [];
  const authorName = msg?.authorName || "User";

  const otherNames = [];

  if (msg.authorId && msg.authorId !== currentUserId) {
    otherNames.push(authorName);
  }

  recipientNames.forEach((name, idx) => {
    const rid = msg.recipientIds?.[idx];
    if (rid && rid !== currentUserId && !otherNames.includes(name)) {
      otherNames.push(name);
    }
  });

  if (otherNames.length === 0) return "Direct Message";
  if (otherNames.length === 1) return otherNames[0];
  return `Group: ${otherNames.join(", ")}`;
};

const toJsDateSafe = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const Home = () => {
  const [jobs, setJobs] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);

  // Lock background scrolling while the Job Details popup is open so the
  // popup can't be scrolled past — it has to be closed to interact with
  // the page behind it.
  useEffect(() => {
    if (!selectedJob) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedJob]);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [jobTotals, setJobTotals] = useState({});
const [showTotalPopup, setShowTotalPopup] = useState(false);
const [totalInput, setTotalInput] = useState("");
const [techLocation, setTechLocation] = useState(null);
const [selectedTech, setSelectedTech] = useState("ALL_TECHS");
const [language, setLanguage] = useState("en");
const [showMoreMenu, setShowMoreMenu] = useState(false);
const navigate = useNavigate();   // ✅ ADD THIS
// ===== Materials Popup =====
const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
const [materialsDraft, setMaterialsDraft] = useState([]);
const [jobMaterials, setJobMaterials] = useState({});
const getMaterialsForJob = (job) =>
  jobMaterials[getMaterialsGroupId(job)] || [];
const [materialsWeeksAhead, setMaterialsWeeksAhead] = useState(1); // 1 or 4
const [showMaterialsNeeded, setShowMaterialsNeeded] = useState(false); // ✅ default hidden
const [showTeamMessaging, setShowTeamMessaging] = useState(false);
const [teamHasUnread, setTeamHasUnread] = useState(false);
const [teamLatestActivityAt, setTeamLatestActivityAt] = useState(null);
const [teamLastSeenAt, setTeamLastSeenAt] = useState(null);
const [teamOpenTaskCount, setTeamOpenTaskCount] = useState(0);
const [teamUnreadLabels, setTeamUnreadLabels] = useState([]);
const [currentUserData, setCurrentUserData] = useState(null);
const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
const [uploadingAvatar, setUploadingAvatar] = useState(false);

const [showContinuePopup, setShowContinuePopup] = useState(false);
const [continueDate, setContinueDate] = useState("");
const [continuingJob, setContinuingJob] = useState(false);

const [showReceiptPopup, setShowReceiptPopup] = useState(false);
const [receiptAmount, setReceiptAmount] = useState("");
const [receiptFile, setReceiptFile] = useState(null);
const [receiptNote, setReceiptNote] = useState("");
const [savingReceipt, setSavingReceipt] = useState(false);

const isAdmin = userRole === "Admin";

const [selectedExportJobIds, setSelectedExportJobIds] = useState([]);

const [techDateView, setTechDateView] = useState("today");
// "today" | "tomorrow" | "week"
const expandedHomeJobs = useMemo(() => {
  const normalOccurrences = expandJobsForHome(jobs).map(
    applyOccurrenceOverrideToRenderedJob
  );

  const movedOccurrences = jobs
    .flatMap(getOccurrenceOverrideRenderJobs)
    .filter((job) => {
      const key = toDateKey(job.__renderDate || job.startDate);
      if (!key || key > endOfNextWeekKey()) return false;

      // These come straight out of the override map, so they get no
      // completed/excluded filtering from the expander. Without this a
      // rescheduled occurrence stayed on Home (and in the summary counts)
      // after being marked complete.
      if (occurrenceIsInDateList(job, job.completedDates)) return false;
      if (occurrenceIsInDateList(job, job.excludedDates)) return false;

      return true;
    });

  return dedupeRenderedJobs(
    mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ])
  );
}, [jobs]);

const expandedCalendarJobs = useMemo(() => {
  const normalOccurrences = expandJobsForCalendar(jobs).map(
    applyOccurrenceOverrideToRenderedJob
  );

  const movedOccurrences = jobs.flatMap(getOccurrenceOverrideRenderJobs);

  return dedupeRenderedJobs(
    mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ])
  );
}, [jobs]);

// 🧱 Materials needed this week (STAFF ONLY)
const weeklyMaterials = useMemo(() => {
  return materialsNeededForWeeks(jobs, materialsWeeksAhead, jobMaterials);
}, [jobs, materialsWeeksAhead, jobMaterials]);

const functions = getFunctions(app, "us-central1");
const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");


const LOCATION_OPTIONS = ["Shop 1", "Property", "Van 1", "Van 2", "Truck 1", "Trailer 1"];

const [openReceivedKey, setOpenReceivedKey] = useState(null); 
// string like `${jobId}_${materialIndex}`

const [receivedLocationDraft, setReceivedLocationDraft] = useState({});
// { "jobId_idx": "Shop 1" }

const HOME_ACTIVE_TAB_STORAGE_KEY = "servicing_home_active_tab";
const VALID_HOME_TABS = [
  "jobs",
  "clock",
  "job-calendar",
  "time-log-calendar",
  "rough-estimate-builder",
  "customer-codes",
  "maps",
  "time-off",
  "clock-time",
];

const [activeTab, setActiveTab] = useState(() => {
  try {
    const stored = sessionStorage.getItem(HOME_ACTIVE_TAB_STORAGE_KEY);
    return VALID_HOME_TABS.includes(stored) ? stored : "jobs";
  } catch (e) {
    return "jobs";
  }
});
// jobs | clock | job-calendar | time-log-calendar | rough-estimate-builder | customer-codes | maps | time-off | clock-time

useEffect(() => {
  try {
    sessionStorage.setItem(HOME_ACTIVE_TAB_STORAGE_KEY, activeTab);
  } catch (e) {
    // sessionStorage unavailable (e.g. private browsing) — safe to ignore
  }
}, [activeTab]);

// Marking a job completed reloads the page so every list re-reads Firestore.
// activeTab is already persisted above, so the reload lands back on the SAME
// tab the user was on (Calendar stays Calendar). This just puts the scroll
// position back too, so the refresh feels like a refresh and not a reset.
useEffect(() => {
  restoreScrollAfterRefresh();
}, []);

const [dateFilter, setDateFilter] = useState(""); // for summary strip filtering
const summary = useMemo(() => {
  const counts = {
    allActive: 0,
    overdue: 0,
    dueToday: 0,
    dueTomorrow: 0,
    dueThisWeek: 0,
    dueNextWeek: 0,
    needsScheduling: 0,
  };

  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const yKey = yesterdayKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  expandedHomeJobs.forEach((job) => {
    counts.allActive++;

    const startKey = getJobStartKey(job);
    if (!startKey) {
      counts.needsScheduling++;
      return;
    }

    if (jobSpansDate(job, tKey)) counts.dueToday++;
    if (jobSpansDate(job, tmKey)) counts.dueTomorrow++;
    if (jobSpansRange(job, tmKey, eowKey)) counts.dueThisWeek++;
    if (jobSpansRange(job, nextWeekStartKey, nextWeekEndKey)) counts.dueNextWeek++;

    // overdue = started before today and still not completed
    if (startKey <= yKey) counts.overdue++;
  });

  return counts;
}, [expandedHomeJobs]);

const isOccurrenceCompleted = (job) =>
  occurrenceIsInDateList(job, job.completedDates);

const getJobNotesForDisplay = (job) => {
  if (!job) return [];

  if (isRecurringJob(job)) {
    const occurrenceKey = getOccurrenceDateKey(job);
    return job.occurrenceNotes?.[occurrenceKey] || [];
  }

  return Array.isArray(job.notes) ? job.notes : [];
};

// Supervisor notes: JobHistory shows the full running list for the job
// (unchanged there), but on a specific occurrence's tile/detail view we
// only want the notes added for THAT occurrence. Notes added before this
// scoping existed have no occurrenceDate tag and are treated as
// belonging to no single occurrence (they stay visible in JobHistory).
const getSupervisorNotesForDisplay = (job) => {
  if (!job) return [];

  const allNotes = Array.isArray(job.supervisorNotes) ? job.supervisorNotes : [];

  if (!isRecurringJob(job)) return allNotes;

  const occurrenceKey = getOccurrenceDateKey(job);
  return allNotes.filter((note) => note?.occurrenceDate === occurrenceKey);
};

const completedTileStyle = {
  background: "#9ca3af",   // gray-400
  color: "#111827",
};


// ⏱️ Clock-in logic (SINGLE SOURCE OF TRUTH)
const clock = useClockInLogic();
// Job check-in / check-out (job_visits). Lives inside the clocked-in day.
const jobVisits = useJobVisits(clock);
const translations = {
  en: {
    jobs: "Jobs",
    jobCalendar: "Job Calendar",
    timeLogCalendar: "Time Log Calendar",
    todayJobs: "Today’s Jobs",
    tomorrowJobs: "Tomorrow’s Jobs",
    weekJobs: "This Week’s Jobs",
    noJobs: "No jobs scheduled for this time frame.",
    startResume: "Start / Resume",
    viewJob: "View Job",
    role: "Role",
    welcome: "Welcome to the Portal",

    // 🔽 ADD THESE
    today: "Today",
    tomorrow: "Tomorrow",
    week: "This Week",
    // Clock
  online: "Online",
  offline: "Offline",
  accuracy: "Accuracy",
  clockIn: "Clock In",
  clockOut: "Clock Out",
  lunchOn: "Lunch On",
  lunchOff: "Lunch Off",
  pastLogs: "Past Time Logs",
  close: "Close",
  currentPeriod: "Current period",
  lastPeriod: "Last period",
  days: "Days",
  hoursNoLunch: "Hours (no lunch)",
  hoursLunch: "Hours (-1h/day lunch)",
  distance: "Distance",
  todaysHours: "Today’s Hours",
  },
  es: {
    jobs: "Trabajos",
    jobCalendar: "Calendario de Trabajos",
    timeLogCalendar: "Calendario de Horas",
    todayJobs: "Trabajos de Hoy",
    tomorrowJobs: "Trabajos de Mañana",
    weekJobs: "Trabajos de Esta Semana",
    noJobs: "No hay trabajos programados para este período.",
    startResume: "Iniciar / Reanudar",
    viewJob: "Ver Trabajo",
    role: "Rol",
    welcome: "Bienvenido al Portal",

    // 🔽 ADD THESE
    today: "Hoy",
    tomorrow: "Mañana",
    week: "Esta Semana",
    // Clock
  online: "En línea",
  offline: "Desconectado",
  accuracy: "Precisión",
  clockIn: "Entrada",
  clockOut: "Salida",
  lunchOn: "Almuerzo Iniciar",
  lunchOff: "Almuerzo Finalizar",
  pastLogs: "Registros de Tiempo",
  close: "Cerrar",
  currentPeriod: "Período actual",
  lastPeriod: "Período anterior",
  days: "Días",
  hoursNoLunch: "Horas (sin almuerzo)",
  hoursLunch: "Horas (-1h/día almuerzo)",
  distance: "Distancia",
  todaysHours: "Horas de Hoy",
  },
};


const t = (key) => translations[language][key] || key;

const getTeamMessagingButtonStyle = () => {
  let bg = "#e0e7ff";

  if (teamHasUnread || teamOpenTaskCount > 0) {
    bg = "#fecaca";
  } else if (showTeamMessaging || teamLastSeenAt) {
    bg = "#93c5fd";
  }

  return {
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: bg,
    color: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    position: "relative",
  };
};

const chip = (bgColor, active = false) => ({
  padding: "6px 12px",
  borderRadius: "16px",
  background: bgColor,
  cursor: "pointer",
  fontWeight: 600,
  border: active ? "2px solid #1d4ed8" : "1px solid transparent",
});

const getJobBucket = (job) => {
  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const yKey = yesterdayKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  const startKey = getJobStartKey(job);

  if (!startKey) return "needsScheduling";
  if (jobSpansDate(job, tKey)) return "today";
  if (jobSpansDate(job, tmKey)) return "tomorrow";
  if (jobSpansRange(job, tmKey, eowKey)) return "week";
  if (jobSpansRange(job, nextWeekStartKey, nextWeekEndKey)) return "nextWeek";
  if (startKey <= yKey) return "overdue";

  return null;
};

const jobRowStyle = (job) => {
  const bucket = getJobBucket(job);

  const colorMap = {
    overdue: "#ffebee",
    today: "#e0f2fe",
    tomorrow: "#fff3cd",
    week: "#e3f2fd",
    nextWeek: "#dbeafe",
    needsScheduling: "#f3e5f5",
  };  

  const bg = bucket ? colorMap[bucket] : "white";

  return {
    border: "1px solid #1d4ed8",
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    cursor: "pointer",
    background: bg,
  };
};


const myJobs = useMemo(() => {
  if (!userFullName) return [];

  return expandedHomeJobs.filter((job) => {
    if (Array.isArray(job.techAssign)) {
      return job.techAssign.includes(userFullName);
    }
    return job.techAssign === userFullName;
  });
}, [expandedHomeJobs, userFullName]);

const matchesTechDateView = (job) => {
  const k = toDateKey(job.startDate);
  if (!k) return false;

  switch (techDateView) {
    case "today":
      return k === todayKey();
    case "tomorrow":
      return k === tomorrowKey();
    case "week":
      return k >= todayKey() && k <= endOfWeekKey();
    default:
      return false;
  }
};

const techUpcomingJobs = useMemo(() => {
  return myJobs.filter(matchesTechDateView);
}, [myJobs, techDateView]);

const techJobsTitle = useMemo(() => {
  switch (techDateView) {
    case "today":
      return t("todayJobs");
    case "tomorrow":
      return t("tomorrowJobs");
    case "week":
      return t("weekJobs");
    default:
      return t("jobs");
  }
}, [techDateView, language]);

const isJobCompleted = (job) => {
  // one-time job
  if (!isRecurringJob(job)) {
    return job.status === "Completed";
  }

  // recurring job — completed only if THIS occurrence is completed.
  // Matches on the occurrence's canonical date first, falling back to the day
  // it was rescheduled to for records written before the canonical-key fix.
  return occurrenceIsInDateList(job, job.completedDates);
};

// True when the signed-in person is on this job's techAssign list.
const isAssignedToMe = (job) => {
  if (!job || !userFullName) return false;
  const ta = job.techAssign;
  return Array.isArray(ta) ? ta.includes(userFullName) : ta === userFullName;
};

// Techs only ever see the jobs they are assigned to (techAssign).
const techVisibleJobs = useMemo(() => {
  if (userRole !== "Tech") return jobs;
  if (!userFullName) return [];
  return jobs.filter((job) => {
    if (isAssignedToMe(job)) return true;
    // A single occurrence may have been reassigned to this tech.
    const overrides =
      job && typeof job.occurrenceOverrides === "object" ? job.occurrenceOverrides : null;
    return Boolean(
      overrides &&
        Object.values(overrides).some((o) =>
          Array.isArray(o?.techAssign)
            ? o.techAssign.includes(userFullName)
            : o?.techAssign === userFullName
        )
    );
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [jobs, userRole, userFullName]);



  // 🔍 search + filter + sort
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [companyWorkOrderSearch, setCompanyWorkOrderSearch] = useState(""); // kept for future use
  const [clientWorkOrderSearch, setClientWorkOrderSearch] = useState(""); // kept for future use

  // NEW: site filter + sort dropdown (string-based)
  const [selectedSite, setSelectedSite] = useState("ALL_SITES");
  const [sortOption, setSortOption] = useState(""); // '', 'siteName', 'startDateAsc', 'startDateDesc', 'cityAsc', 'cityDesc'

  // NEW: filter by specific start date
  const [selectedStartDate, setSelectedStartDate] = useState(""); // "YYYY-MM-DD"

  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  // Job close/delete now routes through the shared closeOrDeleteJob scope
  // dialog (see handleDeleteJob) instead of a separate confirm popup.
  const isTech = userRole === "Tech";
// Admin Portal access (ONLY Admin / Manager)
const canAccessAdminPortal = ["Admin", "Manager"].includes(userRole);

// Staff = Admin + Manager + Assistant
const isStaff = ["Admin", "Manager", "Assistant"].includes(userRole);

// Code tools = Admin + Manager + Assistant + Tech
const canUseCodes = ["Admin", "Manager", "Assistant", "Tech"].includes(userRole);

const canSeeSupervisorNotes = ["Admin", "Manager", "Assistant"].includes(userRole);

// Set when the completion form was submitted, so closing it refreshes.
const completionSubmittedRef = useRef(false);

// Techs must check in to a job before they can complete it. Everyone else
// (office staff completing on a tech's behalf) is not blocked.
const canCompleteJob = (job) => {
  if (userRole === "Tech" && job && !jobVisits.hasVisitFor(job)) {
    alert(
      "Check in to this job first.\n\nTap Check In when you arrive so your time and location are recorded, then mark it completed."
    );
    return false;
  }
  return true;
};


// Destructive actions
const canDelete = ["Admin", "Manager"].includes(userRole);

  

const location = useLocation();

// Coming back from the Job Checklist: reopen the job that was open. This is
// consumed ONCE — it must not re-fire when `jobs` changes later (e.g. after
// completing a job), or it would swap the job under an open popup.
const reopenHandledRef = useRef(null);
useEffect(() => {
  const reopenJobId = location.state?.reopenJobId;
  if (!reopenJobId || !jobs.length) return;
  if (reopenHandledRef.current === location.key) return;

  const occurrenceDate = location.state?.reopenOccurrenceDate || "";
  // Prefer the rendered occurrence (has its own date) over the series record.
  const match =
    (occurrenceDate &&
      expandedHomeJobs.find(
        (job) =>
          job.id === reopenJobId &&
          (job.occurrenceDate || job.startDate || "") === occurrenceDate
      )) ||
    jobs.find((job) => job.id === reopenJobId);

  reopenHandledRef.current = location.key;
  if (match) setSelectedJob(match);
  // Clear the router state so a refresh / later render can't replay it.
  navigate(location.pathname, { replace: true, state: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.key, location.state, jobs.length > 0]);

// ✅ base (Home button color) + active (dark blue)
const topNavBtnStyle = (path, isProtected = false) => {
  const isActive = location.pathname === path;

  return {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: isActive ? "#1d4ed8" : "#e0e7ff", // ✅ default = Home color
    color: isActive ? "#fff" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  };
};
  
  useEffect(() => {
    if (showMaterialsPopup && selectedJob) {
      const groupId = getMaterialsGroupId(selectedJob);
      (async () => {
        const currentMaterials =
          jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));

        const normalizedMaterials = currentMaterials.map((material) => ({
          ...material,
          name: material.name || material.product || "",
          qty: Number(material.qty ?? 1),
          unit: material.unit || "pcs",
          costPer: material.costPer ?? "",
          purchaseLocation: material.purchaseLocation || "",
          notes: material.notes || "",
          purchased: Boolean(material.purchased),
          files: Array.isArray(material.files) ? material.files : [],
        }));

        setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
        setMaterialsDraft(normalizedMaterials);
      })();
    }
  }, [showMaterialsPopup, selectedJob]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;
    
      if (currentUser) {
        setUserId(currentUser.uid);
    
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
    
        // build full users list for Tech dropdown
        const userList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsersList(userList);
    
        // find current user (same logic as before)
        const userDoc = userList.find((u) => u.id === currentUser.uid);
        if (userDoc) {
          setUserRole(userDoc.role);
          setUserFullName(`${userDoc.firstName} ${userDoc.lastName}`);
          setCurrentUserData(userDoc);
        }
      }
    }; 

    const fetchJobs = async () => {
      const jobsRef = collection(db, "jobs");
      const querySnapshot = await getDocs(jobsRef);
    
      const allJobs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    
      const hiddenServiceTypes = ["Approved Proposal", "Denied Proposal"];
    
      const visibleJobs = allJobs.filter(
        (job) => !hiddenServiceTypes.includes(job.serviceType)
      );      
    
      setJobs(visibleJobs);
      const totalsSnap = await getDocs(collection(db, "job_totals"));
const totalsMap = {};
totalsSnap.forEach(doc => {
  totalsMap[doc.id] = doc.data().total;
});
setJobTotals(totalsMap);

      try {
        setJobMaterials(await fetchAllJobMaterials());
      } catch (error) {
        console.error("Error fetching job materials:", error);
      }
    };

    fetchUserData();
    fetchJobs();
  }, []);

  useEffect(() => {
    if (!userId) return;
  
    const seenRef = doc(db, "teamMessagingSeen", userId);
  
    let seenMap = {};
    let latestTaskAt = null;
    let latestConversationItems = [];
  
    const recompute = () => {
      const unreadConversations = latestConversationItems.filter((item) => {
        const seenAt = toJsDateSafe(seenMap[item.key]);
        return item.latestAt && (!seenAt || item.latestAt > seenAt);
      });
  
      setTeamUnreadLabels(unreadConversations.map((x) => x.label));
      setTeamHasUnread(unreadConversations.length > 0);
  
      const latest =
        [
          ...latestConversationItems.map((x) => x.latestAt),
          latestTaskAt,
        ]
          .filter(Boolean)
          .sort((a, b) => b - a)[0] || null;
  
      setTeamLatestActivityAt(latest);
    };
  
    const unsubSeen = onSnapshot(seenRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      seenMap = data?.conversationSeen || {};
      setTeamLastSeenAt(toJsDateSafe(data?.lastSeenAt));
      recompute();
    });
  
    const unsubMessages = onSnapshot(
      query(collection(db, "teamMessages"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const docs = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((msg) => {
            if (msg.audience === "all") return true;
            if (msg.authorId === userId) return true;
            return Array.isArray(msg.recipientIds) && msg.recipientIds.includes(userId);
          });
  
        const convoMap = new Map();
  
        docs.forEach((msg) => {
          if (!msg.createdAt?.toDate) return;
          if (msg.authorId === userId) return; // unread only from others
  
          const key = getTeamConversationKey(msg);
          const label = getTeamConversationLabel(msg, userId);
          const createdAt = msg.createdAt.toDate();
  
          const current = convoMap.get(key);
          if (!current || createdAt > current.latestAt) {
            convoMap.set(key, { key, label, latestAt: createdAt });
          }
        });
  
        latestConversationItems = Array.from(convoMap.values());
        recompute();
      }
    );
  
    const unsubTasks = onSnapshot(
      query(collection(db, "teamTasks"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  
        const openTasks = docs.filter((task) => task.status !== "Completed");
        setTeamOpenTaskCount(openTasks.length);
  
        const latestOtherTask = docs.find((task) => {
          const createdByOther =
            task.createdById &&
            task.createdById !== userId &&
            task.createdAt?.toDate;
  
          const completedByOther =
            task.completedById &&
            task.completedById !== userId &&
            task.completedAt?.toDate;
  
          return createdByOther || completedByOther;
        });
  
        const createdAt =
          latestOtherTask?.createdById &&
          latestOtherTask?.createdById !== userId &&
          latestOtherTask?.createdAt?.toDate
            ? latestOtherTask.createdAt.toDate()
            : null;
  
        const completedAt =
          latestOtherTask?.completedById &&
          latestOtherTask?.completedById !== userId &&
          latestOtherTask?.completedAt?.toDate
            ? latestOtherTask.completedAt.toDate()
            : null;
  
        latestTaskAt = [createdAt, completedAt].filter(Boolean).sort((a, b) => b - a)[0] || null;
  
        recompute();
      }
    );
  
    return () => {
      unsubSeen();
      unsubMessages();
      unsubTasks();
    };
  }, [userId]);

  // 🔽 build unique site list from jobs
  const siteOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => {
      const n = (j.siteName || "").trim();
      if (n) set.add(n);
    });
    return ["ALL_SITES", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [jobs]);

  useEffect(() => {
    if (activeTab !== "jobs") {
      setDateFilter("");
      setSelectedStartDate("");
    }
  }, [activeTab]);
  
  useEffect(() => {
    filterAndSortJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, expandedHomeJobs, searchTerm, selectedSite, sortOption, selectedStartDate, dateFilter, selectedTech]);  

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
  
    try {
      setUploadingAvatar(true);
  
      const storage = getStorage();
      const avatarRef = ref(
        storage,
        `users/${userId}/avatar/${Date.now()}_${file.name}`
      );
  
      await uploadBytes(avatarRef, file);
      const avatarURL = await getDownloadURL(avatarRef);
  
      await updateDoc(doc(db, "users", userId), { avatarURL });
  
      setCurrentUserData((prev) => ({
        ...prev,
        avatarURL,
      }));
  
      setUsersList((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, avatarURL } : u
        )
      );
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("Could not upload avatar.");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const closeModal = () => {
    setSelectedJob(null);
    setIsEditing(false);
  };
  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const isProjectJob = (job) => {
    return job?.isProject === true && job?.projectStatus !== "Project Completed";
  };
  
  const getNextProjectDate = (job) => {
    const baseDate = toDateKey(job?.startDate) || todayKey();
    return moment(baseDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  };

  const getContinuedWO = (baseWO) => {
    if (!baseWO) return "";
  
    const base = String(baseWO).replace(/-\d+$/, "");
  
    const related = jobs.filter((j) => {
      const wo = String(j.wo || "");
      return wo === base || wo.startsWith(`${base}-`);
    });
  
    const nextNumber = related.length + 1;
  
    return `${base}-${nextNumber}`;
  };
  
  const continueJobToNewDate = async () => {
    if (!selectedJob?.id || !continueDate) {
      alert("Please select the new date.");
      return;
    }
  
    const oldDate = toDateKey(selectedJob.startDate);
  
    if (continueDate === oldDate) {
      alert("The continue date must be different from the original start date.");
      return;
    }
  
    setContinuingJob(true);
  
    try {
      const oldJobRef = doc(db, "jobs", selectedJob.id);
  
      const newWO = getContinuedWO(selectedJob.wo);
  
      const copiedJob = {
        ...selectedJob,
  
        // remove fields that should not copy as Firestore document fields
        id: undefined,
        __renderDate: undefined,
        occurrenceDate: undefined,
        newFiles: undefined,
  
        // new independent job
        wo: newWO || selectedJob.wo || "",
        startDate: continueDate,
        endDate: continueDate,
        status: "Active",
        recurrence: { type: "none" },
  
        // keep record of where it came from, but it is NOT linked for edits
        continuedFromJobId: selectedJob.id,
        materialsGroupId: getMaterialsGroupId(selectedJob),
        originalStartDate: oldDate || selectedJob.originalStartDate || "",
        continuedFromDate: oldDate || "",
  
        notes: [],
        receipts: [],
        receiptsTotal: 0,
        completedDates: [],
        occurrenceNotes: {},
  
        creationDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByContinue: true,
        createdBy: {
          uid: userId || "",
          firstName: userFullName?.split(" ")?.[0] || "",
          lastName: userFullName?.split(" ")?.slice(1).join(" ") || "",
          fullName: userFullName || "User",
        },
      };
  
      if (copiedJob.jobPlan) {
        copiedJob.jobPlan = { ...copiedJob.jobPlan };
        delete copiedJob.jobPlan.materials;
      }

      Object.keys(copiedJob).forEach((key) => {
        if (copiedJob[key] === undefined) delete copiedJob[key];
      });
  
      await updateDoc(oldJobRef, {
        status: "Completed",
        completedDates: arrayUnion(oldDate || todayKey()),
        continuedToDate: continueDate,
        continuedToWO: newWO || selectedJob.wo || "",
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      const newDocRef = await addDoc(collection(db, "jobs"), copiedJob);
  
      const completedOldJob = {
        ...selectedJob,
        status: "Completed",
        completedDates: [
          ...(selectedJob.completedDates || []),
          oldDate || todayKey(),
        ],
        continuedToDate: continueDate,
        continuedToWO: newWO || selectedJob.wo || "",
      };
  
      const newJobForState = {
        ...copiedJob,
        id: newDocRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      };
  
      setJobs((prev) => [
        ...prev.map((job) =>
          job.id === selectedJob.id ? completedOldJob : job
        ),
        newJobForState,
      ]);
  
      setSelectedJob(null);
      setShowContinuePopup(false);
      setContinueDate("");
  
      alert(`Original job completed. New job scheduled for ${continueDate}.`);
    } catch (err) {
      console.error("Error continuing job:", err);
      alert("Could not continue this job.");
    } finally {
      setContinuingJob(false);
    }
  };

  const completeProjectDayAndCreateNext = async () => {
    if (!selectedJob?.id) return;
  
    const ok = window.confirm(
      "Complete this project day and create the next active project day?"
    );
  
    if (!ok) return;
  
    const oldDate = toDateKey(selectedJob.startDate) || todayKey();
    const nextDate = getNextProjectDate(selectedJob);
    const oldJobRef = doc(db, "jobs", selectedJob.id);
    const newWO = getContinuedWO(selectedJob.wo);
  
    try {
      const projectRootId =
        selectedJob.projectRootId || selectedJob.continuedFromJobId || selectedJob.id;
  
      const copiedJob = {
        ...selectedJob,
  
        id: undefined,
        __renderDate: undefined,
        occurrenceDate: undefined,
        newFiles: undefined,
  
        wo: newWO || selectedJob.wo || "",
        startDate: nextDate,
        endDate: nextDate,
        status: "Active",
        recurrence: { type: "none" },
  
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectDayNumber: Number(selectedJob.projectDayNumber || 1) + 1,
  
        projectContinuedFromJobId: selectedJob.id,
        projectContinuedFromDate: oldDate,
        materialsGroupId: projectRootId,
  
        // keep project history visible on each new day
        notes: Array.isArray(selectedJob.notes) ? selectedJob.notes : [],
        fileURLs: Array.isArray(selectedJob.fileURLs) ? selectedJob.fileURLs : [],
        fileNames: Array.isArray(selectedJob.fileNames) ? selectedJob.fileNames : [],
        jobPlan: (() => {
          const plan = { ...(selectedJob.jobPlan || {}) };
          delete plan.materials;
          return plan;
        })(),
  
        // keep receipts separate by day
        receipts: [],
        receiptsTotal: 0,
  
        completedDates: [],
        occurrenceNotes: {},
  
        creationDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByProjectContinue: true,
        createdBy: {
          uid: userId || "",
          firstName: userFullName?.split(" ")?.[0] || "",
          lastName: userFullName?.split(" ")?.slice(1).join(" ") || "",
          fullName: userFullName || "User",
        },
      };
  
      Object.keys(copiedJob).forEach((key) => {
        if (copiedJob[key] === undefined) delete copiedJob[key];
      });
  
      await updateDoc(oldJobRef, {
        status: "Completed",
        completedDates: arrayUnion(oldDate),
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectContinuedToDate: nextDate,
        projectContinuedToWO: newWO || selectedJob.wo || "",
        updatedAt: serverTimestamp(),
      });
  
      const newDocRef = await addDoc(collection(db, "jobs"), copiedJob);
  
      await updateDoc(oldJobRef, {
        projectContinuedToJobId: newDocRef.id,
      });
  
      const completedOldJob = {
        ...selectedJob,
        status: "Completed",
        completedDates: [...(selectedJob.completedDates || []), oldDate],
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectContinuedToDate: nextDate,
        projectContinuedToWO: newWO || selectedJob.wo || "",
        projectContinuedToJobId: newDocRef.id,
      };
  
      const newJobForState = {
        ...copiedJob,
        id: newDocRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      };
  
      setJobs((prev) => [
        ...prev.map((job) =>
          job.id === selectedJob.id ? completedOldJob : job
        ),
        newJobForState,
      ]);
  
      setShowVerificationPopup(false);
      setSelectedJob(null);
  
      alert(`Project day completed. New project day created for ${nextDate}.`);
    } catch (err) {
      console.error("Error completing project day:", err);
      alert("Could not complete this project day.");
    }
  };

  const markProjectFullyCompleted = async () => {
    if (!selectedJob?.id) return;
  
    const completionDate = window.prompt(
      "Enter the final project completion date:",
      todayKey()
    );
  
    if (!completionDate) return;
  
    const ok = window.confirm(
      `Mark this entire project completed as of ${completionDate}? This will not create another project day.`
    );
  
    if (!ok) return;
  
    try {
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        status: "Completed",
        isProject: true,
        projectStatus: "Project Completed",
        projectCompletedAt: completionDate,
        projectCompletedBy: userFullName || "User",
        completedDates: arrayUnion(completionDate),
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? {
                ...job,
                status: "Completed",
                isProject: true,
                projectStatus: "Project Completed",
                projectCompletedAt: completionDate,
                projectCompletedBy: userFullName || "User",
                completedDates: [...(job.completedDates || []), completionDate],
              }
            : job
        )
      );
  
      setSelectedJob(null);
      alert("Project marked completed.");
    } catch (err) {
      console.error("Error completing project:", err);
      alert("Could not mark project completed.");
    }
  };


  const markSourceProposalCompleted = async (job) => {
    if (!job?.proposalId) return;

    try {
      await updateDoc(doc(db, "proposals", job.proposalId), {
        status: "Completed",
        completedFromJobId: job.id || "",
        completedFromWO: job.wo || "",
        proposalCompletedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Could not mark source proposal completed:", err);
    }
  };

  const markJobCompleted = async () => {
    if (!selectedJob) return;
  
    const jobRef = doc(db, "jobs", selectedJob.id);
    // NOT getOccurrenceDateKey() — that returns the day this occurrence was
    // rescheduled to. completedDates has to be keyed by the date the
    // recurrence RULE produced, because that's the key every expander,
    // excludedDates and occurrenceOverrides already use.
    const occurrenceKey = getCompletionOccurrenceKey(selectedJob);
  
    try {
      if (isProjectJob(selectedJob)) {
        await completeProjectDayAndCreateNext();
        return;
      }
      if (isRecurringJob(selectedJob)) {
        // Recurring: close ONLY this date. completedDates drives three
        // things at once —
        //   • Home hides this occurrence (isJobCompleted)
        //   • Calendar strikes this one date red (occurrenceStatus)
        //   • SendInvoices surfaces this one date as an invoiceable row
        // …while every other date in the series stays open.
        await updateDoc(jobRef, {
          completedDates: arrayUnion(occurrenceKey),
          [`completedAtByDate.${occurrenceKey}`]: new Date().toISOString(),
          updatedAt: serverTimestamp(),
        });

        setJobs(prev =>
          prev.map(j =>
            j.id === selectedJob.id
              ? { ...j, completedDates: [...(j.completedDates || []), occurrenceKey] }
              : j
          )
        );

        setShowVerificationPopup(false);
        setSelectedJob(null);

        // Full refresh so Calendar / SendInvoices / Job History all pick the
        // change up. activeTab is in sessionStorage, so we come back to this
        // same tab.
        refreshKeepingView();
        return;
      }
      
  
      // ✅ ONE-TIME JOB
      await updateDoc(jobRef, {
        status: "Completed",
        completedDates: arrayUnion(occurrenceKey),
        completedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      // 🔥 update LOCAL STATE
      setJobs(prev =>
        prev.map(j =>
          j.id === selectedJob.id
            ? {
                ...j,
                status: "Completed",
                completedDates: [...(j.completedDates || []), occurrenceKey],
                completedAt: new Date().toISOString(),
              }
            : j
        )
      );
  
      setShowVerificationPopup(false);
      setSelectedJob(null);

      refreshKeepingView();
    } catch (err) {
      console.error("Error marking job completed:", err);
      alert("Could not mark job completed.");
    }
  };
  
  
  const openTotalPopup = (job) => {
    const jobKey = String(job.wo ?? job.id);
  
    setSelectedJob(job);
    setTotalInput(jobTotals[jobKey] ?? "");
    setShowTotalPopup(true);
  };  
  
  const saveTotal = async () => {
    if (!selectedJob || totalInput === "") return;
  
    // 🔒 ALWAYS force string ID
    const jobKey = String(selectedJob.wo ?? selectedJob.id);
  
    await addJobTotal({
      jobId: jobKey,
      wo: selectedJob.wo ? String(selectedJob.wo) : null,
      proposalId: selectedJob.proposalId || null,
      total: Number(totalInput),
      source: "job",
    });

    // keep job.estimatedTotal in sync too, same as the SendInvoices page,
    // so anything still reading estimatedTotal directly stays correct
    await updateDoc(doc(db, "jobs", selectedJob.id), {
      estimatedTotal: Number(totalInput),
    });
  
    // update UI instantly
    setJobTotals(prev => ({
      ...prev,
      [jobKey]: Number(totalInput),
    }));
  
    setShowTotalPopup(false);
  };
  
  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      {
        name: "",
        qty: 1,
        unit: "pcs",
        costPer: "",
        purchaseLocation: "",
        notes: "",
        purchased: false,
        files: [],
      },
    ]);
  };

  const updateMaterialDraft = (index, patch) => {
    setMaterialsDraft((prev) =>
      prev.map((material, materialIndex) =>
        materialIndex === index ? { ...material, ...patch } : material
      )
    );
  };

  const removeMaterialDraft = (index) => {
    setMaterialsDraft((prev) =>
      prev.filter((_, materialIndex) => materialIndex !== index)
    );
  };

  const uploadMaterialFiles = async (index, fileList) => {
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];

    for (const file of files) {
      const fileRef = ref(
        getStorage(),
        `materialFiles/${groupId}/${Date.now()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      uploaded.push({ url, name: file.name });
    }

    setMaterialsDraft((previous) =>
      previous.map((material, materialIndex) =>
        materialIndex === index
          ? { ...material, files: [...(material.files || []), ...uploaded] }
          : material
      )
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? {
              ...material,
              files: (material.files || []).filter((_, fi) => fi !== fileIndex),
            }
          : material
      )
    );
  };

  const getEstimatedMaterialCost = (material) => {
    const qty = Number(material?.qty || 0);
    const costPer = Number(material?.costPer || 0);
    return qty * costPer;
  };
  
  const handleAdminPortalClick = () => {
    if (!canAccessAdminPortal) {
      window.alert("🚫 Access denied: You don’t have permission to view the Admin Portal.");
      navigate("/"); // back to Home
      return;
    }
    navigate("/SupervisorDashboard");
  };
  
  const updateMaterialStatus = async (jobId, materialIndex, patch) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;

    const groupId = getMaterialsGroupId(job);
    const currentMaterials = jobMaterials[groupId] || (await loadMaterialsForJob(job));
    const nextMaterials = currentMaterials.map((m, idx) =>
      idx === materialIndex ? { ...m, ...patch } : m
    );

    await saveMaterialsForJob(job, nextMaterials);

    // keep UI in sync instantly
    setJobMaterials((prev) => ({ ...prev, [groupId]: nextMaterials }));
  };
  
  const saveMaterials = async () => {
    if (!selectedJob?.id) return;
  
    try {
      const groupId = getMaterialsGroupId(selectedJob);

      await saveMaterialsForJob(selectedJob, materialsDraft);

      // ✅ Keep UI in sync (both list + popup job)
      setJobMaterials((prev) => ({ ...prev, [groupId]: materialsDraft }));

      setShowMaterialsPopup(false);
    } catch (err) {
      console.error("Error saving materials:", err);
      alert("Could not save materials. Please try again.");
    }
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setSelectedJob((prev) => ({
      ...prev,
      newFiles: files.map((file) => URL.createObjectURL(file)),
    }));
  };

  const uploadFiles = async (files) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const saveReceipt = async () => {
    if (!selectedJob?.id) return;
  
    if (!receiptAmount && !receiptFile) {
      alert("Please add a receipt amount or upload a receipt image.");
      return;
    }
  
    setSavingReceipt(true);
  
    try {
      let fileURL = "";
      let fileName = "";
  
      if (receiptFile) {
        const storage = getStorage();
        const fileRef = ref(
          storage,
          `jobReceipts/${selectedJob.id}/${Date.now()}_${receiptFile.name}`
        );
  
        await uploadBytes(fileRef, receiptFile);
        fileURL = await getDownloadURL(fileRef);
        fileName = receiptFile.name;
      }
  
      const newReceipt = {
        amount: Number(receiptAmount || 0),
        note: receiptNote || "",
        fileURL,
        fileName,
        uploadedBy: userFullName || "User",
        uploadedAt: new Date().toISOString(),
      };
  
      const updatedReceipts = Array.isArray(selectedJob.receipts)
        ? [...selectedJob.receipts, newReceipt]
        : [newReceipt];
  
      const receiptsTotal = updatedReceipts.reduce(
        (sum, receipt) => sum + Number(receipt.amount || 0),
        0
      );
  
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        receipts: updatedReceipts,
        receiptsTotal,
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, receipts: updatedReceipts, receiptsTotal }
            : job
        )
      );
  
      setFilteredJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, receipts: updatedReceipts, receiptsTotal }
            : job
        )
      );
  
      setSelectedJob((prev) => ({
        ...prev,
        receipts: updatedReceipts,
        receiptsTotal,
      }));
  
      setReceiptAmount("");
      setReceiptFile(null);
      setReceiptNote("");
      setShowReceiptPopup(false);
    } catch (err) {
      console.error("Error saving receipt:", err);
      alert("Could not save receipt.");
    } finally {
      setSavingReceipt(false);
    }
  };

  const addSupervisorNote = async (jobOverride = null) => {
    const targetJob = jobOverride || selectedJob;
    if (!targetJob?.id) return;
  
    if (!canSeeSupervisorNotes) {
      alert("You do not have permission to add supervisor notes.");
      return;
    }
  
    const text = window.prompt("Add supervisor note:");
    if (!text || !text.trim()) return;
  
    const isRecurring = isRecurringJob(targetJob);
    const occurrenceKey = isRecurring ? getOccurrenceDateKey(targetJob) : null;

    const newSupervisorNote = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: text.trim(),
      author: userFullName || "User",
      authorRole: userRole || "",
      createdAt: new Date().toISOString(),
      checked: false,
      checkedBy: "",
      checkedAt: "",
      occurrenceDate: occurrenceKey,
    };
  
    const updatedSupervisorNotes = Array.isArray(targetJob.supervisorNotes)
      ? [...targetJob.supervisorNotes, newSupervisorNote]
      : [newSupervisorNote];
  
    await updateDoc(doc(db, "jobs", targetJob.id), {
      supervisorNotes: updatedSupervisorNotes,
    });
  
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === targetJob.id
          ? { ...job, supervisorNotes: updatedSupervisorNotes }
          : job
      )
    );
  
    setFilteredJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === targetJob.id
          ? { ...job, supervisorNotes: updatedSupervisorNotes }
          : job
      )
    );
  
    setSelectedJob((prev) => ({
      ...prev,
      supervisorNotes: updatedSupervisorNotes,
    }));
  };

  const saveNote = async () => {
    if (!selectedJob || (noteText.trim() === "" && selectedFiles.length === 0)) {
      return;
    }
  
    setUploading(true);
  
    try {
      const occurrenceKey = getOccurrenceDateKey(selectedJob);
      const isRecurring = isRecurringJob(selectedJob);
  
      let fileUploads = [];
  
      if (selectedFiles.length > 0) {
        const storage = getStorage();
  
        fileUploads = await Promise.all(
          selectedFiles.map(async (file) => {
            const folderKey = isRecurring ? occurrenceKey : "main";
  
            const fileRef = ref(
              storage,
              `notes/${selectedJob.id}/${folderKey}/${Date.now()}_${file.name}`
            );
  
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
  
            return { fileURL: url, fileName: file.name };
          })
        );
      }
  
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName || "User",
        files: fileUploads,
        occurrenceDate: isRecurring ? occurrenceKey : null,
      };
  
      const jobDocRef = doc(db, "jobs", selectedJob.id);
  
      if (isRecurring) {
        const freshSnap = await getDoc(jobDocRef);
        const freshData = freshSnap.exists() ? freshSnap.data() : {};
  
        const existingOccurrenceNotes = freshData.occurrenceNotes || {};
        const existingNotesForDate = existingOccurrenceNotes[occurrenceKey] || [];
  
        const updatedOccurrenceNotes = {
          ...existingOccurrenceNotes,
          [occurrenceKey]: [...existingNotesForDate, newNote],
        };
  
        await updateDoc(jobDocRef, {
          occurrenceNotes: updatedOccurrenceNotes,
        });
  
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id
              ? { ...job, occurrenceNotes: updatedOccurrenceNotes }
              : job
          )
        );
  
        setFilteredJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id
              ? { ...job, occurrenceNotes: updatedOccurrenceNotes }
              : job
          )
        );
  
        setSelectedJob((prev) => ({
          ...prev,
          occurrenceNotes: updatedOccurrenceNotes,
        }));
      } else {
        const updatedNotes = Array.isArray(selectedJob.notes)
          ? [...selectedJob.notes, newNote]
          : [newNote];
  
        await updateDoc(jobDocRef, { notes: updatedNotes });

        try {
          await sendJobNoteEmailCallable({
            jobId: selectedJob.id,
            noteIndex: updatedNotes.length - 1,
          });
        } catch (emailErr) {
          console.error("Note saved, but note email failed:", emailErr);
        }
  
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, notes: updatedNotes } : job
          )
        );
  
        setFilteredJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, notes: updatedNotes } : job
          )
        );
  
        setSelectedJob((prev) => ({
          ...prev,
          notes: updatedNotes,
        }));
      }
  
      setNoteText("");
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (err) {
      console.error("Error saving note:", err);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, "days");

    if (diffDays > 30) {
      return "red";
    } else if (diffDays > 1) {
      return "orange";
    } else {
      return "white";
    }
  };
  const rightNavTabs = [
    { label: "Clock Time", tab: "clock-time", path: "/ClockTime" },
    { label: "Customer Codes", tab: "customer-codes", path: "/CustomerCodes" },
    { label: "Maps", tab: "maps", path: "/Maps" },
    { label: "Time Off", tab: "time-off", path: "/TimeOff" },
  ];
  
  
  const navTabStyle = {
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  
    minWidth: 140,              // ✅ prevents squishing
    flex: "0 0 auto",           // ✅ keeps button size stable
  };  
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCompanyWorkOrderSearchChange = (e) => {
    setCompanyWorkOrderSearch(e.target.value);
  };

  const handleClientWorkOrderSearchChange = (e) => {
    setClientWorkOrderSearch(e.target.value);
  };

  const getReceiptsTotal = (job) => {
    if (!Array.isArray(job?.receipts)) return 0;
  
    return job.receipts.reduce((sum, receipt) => {
      return sum + Number(receipt.amount || 0);
    }, 0);
  };

  // 🔍 helper like WorkOrders
  const normalize = (v) => (v || "").toString().toLowerCase();

  const matchesSearch = (job) => {
    const term = normalize(searchTerm);
    if (!term) return true;
  
    return (
      normalize(job.siteName).includes(term) ||
      normalize(job.city).includes(term) ||
      normalize(job.state).includes(term) ||
      normalize(job.scope).includes(term) ||
  
      // ✅ WO searches (support alt field names just in case)
      normalize(job.wo).includes(term) ||
      normalize(job.workOrder).includes(term) ||
      normalize(job.workOrderNumber).includes(term) ||
  
      normalize(job.clientWorkOrder).includes(term)
    );
  };
  

  const handleSortDropdownChange = (e) => {
    setSortOption(e.target.value);
  };

  const filterAndSortJobs = () => {
    let data = [...expandedHomeJobs];
// ✅ ALWAYS show Active-only in list (hide completed one-time + completed occurrences)
data = data.filter((job) => !isJobCompleted(job));

// ✅ hide invoice-sent one-time jobs from the Jobs tab list.
// (For recurring series, job.status lives on the shared master doc and
// gets set to "Invoice Sent" when ANY single occurrence is invoiced —
// see SendInvoices.js handleInvoiceSent — so it must not hide the whole
// series here.)
data = data.filter((job) => isRecurringJob(job) || job.status !== "Invoice Sent");


    // filter by site
    if (selectedSite !== "ALL_SITES") {
      data = data.filter((j) => (j.siteName || "") === selectedSite);
    }

    // filter by technician (Admin)
if (selectedTech !== "ALL_TECHS") {
  data = data.filter((job) => {
    if (Array.isArray(job.techAssign)) {
      return job.techAssign.includes(selectedTech);
    }
    return job.techAssign === selectedTech;
  });
}

// 🔴 date-based summary filter
if (dateFilter) {
  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const yKey = yesterdayKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  data = data.filter((job) => {
    const startKey = getJobStartKey(job);

    switch (dateFilter) {
      case "today":
        return jobSpansDate(job, tKey);

      case "tomorrow":
        return jobSpansDate(job, tmKey);

      case "week":
        return jobSpansRange(job, tmKey, eowKey);

      case "nextWeek":
        return jobSpansRange(job, nextWeekStartKey, nextWeekEndKey);

      case "overdue":
        return startKey && startKey <= yKey;

      case "needsScheduling":
        return !startKey;

      case "allActive":
        return true;

      default:
        return true;
    }
  });
}
    // filter by specific start date (if selected)
    if (selectedStartDate) {
      data = data.filter((j) =>
        toDateKey(j.startDate) === selectedStartDate
      );
    }
    

    // search
    data = data.filter(matchesSearch);

    // sort
    // 🔤 DEFAULT SORT: Site Name A → Z
data.sort((a, b) =>
  (a.siteName || "").localeCompare(b.siteName || "", undefined, {
    sensitivity: "base",
  })
);

// 🔁 USER-SELECTED SORT OVERRIDES DEFAULT
if (sortOption === "startDateAsc") {
  data.sort(
    (a, b) =>
      moment(toDateKey(a.startDate)).valueOf() -
      moment(toDateKey(b.startDate)).valueOf()
  );
} else if (sortOption === "startDateDesc") {
  data.sort(
    (a, b) =>
      moment(toDateKey(b.startDate)).valueOf() -
      moment(toDateKey(a.startDate)).valueOf()
  );
}
 else if (sortOption === "cityAsc") {
  data.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
} else if (sortOption === "cityDesc") {
  data.sort((a, b) => (b.city || "").localeCompare(a.city || ""));
}


    setFilteredJobs(data);
  };

  const saveQuickJobFields = async (changes) => {
    if (!selectedJob?.id) return;

    const editedQuickJob = { ...selectedJob, ...changes };
    const result = await saveRecurringJobEdit({
      originalJob: selectedJob,
      editedJob: editedQuickJob,
    });

    if (result.mode === "cancel") return false;

    let nextSelectedJob = editedQuickJob;
    if (result.mode === "future") {
      setJobs((prev) => [
        ...prev.map((job) => (job.id === result.oldJob.id ? result.oldJob : job)),
        result.newJob,
      ]);
      nextSelectedJob = result.newJob;
    } else if (result.mode === "occurrence") {
      setJobs((prev) =>
        prev.map((job) => (job.id === result.masterJob.id ? result.masterJob : job))
      );
      nextSelectedJob = result.updatedJob;
    } else {
      setJobs((prev) =>
        prev.map((job) => (job.id === result.updatedJob.id ? result.updatedJob : job))
      );
      nextSelectedJob = result.updatedJob;
    }

    setSelectedJob(nextSelectedJob);
    setEditedJob(nextSelectedJob);
    return true;
  };

  // ✅ FIX: Save button in edit form – no form submit
  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();
    if (!editedJob?.id || !selectedJob) return;

    const mismatch = getDurationMismatch(editedJob);

    if (mismatch) {
      const ok = window.confirm(
        `Scheduled for ${mismatch.scheduledDays} days but estimated ${mismatch.estimatedDays} days.\n\nSave anyway?`
      );

      if (!ok) return;
    }

    try {
      const result = await saveRecurringJobEdit({
        originalJob: selectedJob,
        editedJob,
      });

      if (result.mode === "cancel") return;

      if (result.mode === "future") {
        setJobs((prevJobs) => [
          ...prevJobs.map((job) =>
            job.id === result.oldJob.id ? result.oldJob : job
          ),
          result.newJob,
        ]);

        setSelectedJob(result.newJob);
        setEditedJob(result.newJob);
      } else if (result.mode === "occurrence") {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.masterJob.id ? result.masterJob : job
          )
        );

        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      } else {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.updatedJob.id ? result.updatedJob : job
          )
        );

        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      }

      setIsEditing(false);
      alert("Job updated successfully!");
    } catch (error) {
      console.error("Error updating job:", error);
      alert("Failed to update job.");
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJob) return;
    try {
      const result = await closeOrDeleteJob({ job: selectedJob });

      if (result.mode === "cancel") return;

      if (result.mode === "deleted") {
        setJobs((prev) => prev.filter((job) => job.id !== result.jobId));
        setSelectedJob(null);
        alert("Job deleted successfully.");
        return;
      }

      // "occurrence" or "future" - job document still exists, just updated.
      setJobs((prev) =>
        prev.map((job) =>
          job.id === result.updatedJob.id ? { ...job, ...result.updatedJob } : job
        )
      );
      setSelectedJob(null);
      alert(
        result.mode === "future"
          ? "This job's future occurrences have been closed. Its history is unchanged."
          : "This occurrence has been closed."
      );
    } catch (error) {
      console.error("Error closing job:", error);
      alert("Failed to close this job.");
    }
  };

  const toggleExportJob = (jobKey) => {
    setSelectedExportJobIds((prev) =>
      prev.includes(jobKey)
        ? prev.filter((id) => id !== jobKey)
        : [...prev, jobKey]
    );
  };
  
  const getJobExportKey = (job) =>
    `${job.id}_${job.occurrenceDate || toDateKey(job.startDate) || "UNSCHEDULED"}`;
  
  const getExportJobTotal = (job) => {
    const candidates = [
      jobTotals?.[job.id],
      job?.estimatedTotal,
      job?.jobTotal,
      job?.total,
      job?.amount,
    ];

    const found = candidates.find(
      (value) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );

    return found === undefined ? 0 : Number(found);
  };

  const inferExportSkills = (job = {}) => {
    const text = `${job.serviceType || ""} ${job.scope || ""} ${job.jobDescription || ""}`.toLowerCase();

    const rules = [
      ["Electrical", ["electric", "outlet", "switch", "breaker", "light fixture", "gfci"]],
      ["Plumbing", ["plumb", "pipe", "toilet", "sink", "faucet", "hose bib", "water heater"]],
      ["HVAC", ["hvac", "air conditioning", "heat pump", "furnace"]],
      ["Painting", ["paint", "primer", "stain"]],
      ["Drywall", ["drywall", "sheetrock", "gypsum", "patch wall"]],
      ["Framing", ["framing", "frame", "stud", "rough carpentry"]],
      ["Carpentry", ["carpentry", "trim", "cabinet", "shelving", "wood"]],
      ["Flooring", ["floor", "lvp", "vinyl plank", "hardwood", "carpet"]],
      ["Tile", ["tile", "grout", "backsplash", "shower pan"]],
      ["Concrete", ["concrete", "sidewalk", "curb", "slab"]],
      ["Masonry", ["brick", "block", "masonry", "stone", "cmu"]],
      ["Roofing", ["roof", "shingle", "flashing"]],
      ["Fencing", ["fence", "gate", "picket", "post"]],
      ["Decking", ["deck", "railing", "joist"]],
      ["Landscaping", ["landscape", "mulch", "tree", "plant"]],
      ["Drainage", ["drain", "downspout", "french drain"]],
      ["Cleaning", ["clean", "janitorial"]],
      ["Bulk Trash", ["bulk trash", "trash out", "debris removal", "haul"]],
      ["Demo", ["demo", "demolition", "remove"]],
    ];

    const matches = rules
      .filter(([, keywords]) => keywords.some((keyword) => text.includes(keyword)))
      .map(([skill]) => skill);

    return matches.length
      ? Array.from(new Set(matches))
      : [job.serviceType || "Maintenance"];
  };

  const buildHomeExportRecommendation = (job, workingJobs = []) => {
    const today = moment().startOf("day");
    const startKey = toDateKey(job.startDate);
    const isPastDue = Boolean(
      startKey && moment(startKey, "YYYY-MM-DD").isBefore(today, "day")
    );
    const isUnscheduled = !startKey;

    if (!isPastDue && !isUnscheduled) return null;

    const requiredSkills = inferExportSkills(job);
    // Honors the Show In Dropdowns / Deactivated flags from Staff Settings.
    const activeStaff = filterStaffForDropdowns(usersList)
      .filter((user) => (user.status || "Active") !== "Inactive")
      .filter((user) => !user.hideFromScheduling)
      .map((user) => ({
        ...user,
        displayName:
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.name ||
          user.email ||
          "",
      }))
      .filter((user) => user.displayName);

    const normalize = (value = "") =>
      String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const skillMatches = activeStaff.filter((user) => {
      const role = normalize(user.role);
      const position = normalize(user.position);
      const skills = Array.isArray(user.skills)
        ? user.skills.map(normalize)
        : [];

      if (
        role === "supervisor" ||
        position.includes("supervisor") ||
        skills.includes("all")
      ) {
        return true;
      }

      return requiredSkills.some((required) => {
        const req = normalize(required);
        return skills.some(
          (skill) =>
            skill === req ||
            skill.includes(req) ||
            req.includes(skill)
        );
      });
    });

    const candidates = skillMatches.length ? skillMatches : activeStaff;
    if (!candidates.length) return null;

    const estimatedDays = Math.max(
      1,
      Number(job.jobPlan?.estimatedDays || 1)
    );
    const laborNeeded = Math.max(
      1,
      Number(job.jobPlan?.estimatedLabor || 1)
    );

    const assignedByDate = new Map();

    workingJobs.forEach((existingJob) => {
      if (
        ["Completed", "Invoice Sent", "Deleted"].includes(existingJob.status)
      ) {
        return;
      }

      const dateKey = toDateKey(
        existingJob.startDate || existingJob.occurrenceDate
      );
      if (!dateKey) return;

      const names = Array.isArray(existingJob.techAssign)
        ? existingJob.techAssign.filter(Boolean)
        : existingJob.techAssign
          ? [existingJob.techAssign]
          : [];

      if (!assignedByDate.has(dateKey)) {
        assignedByDate.set(dateKey, new Set());
      }

      names.forEach((name) => assignedByDate.get(dateKey).add(name));
    });

    const cursor = today.clone();

    for (let searched = 0; searched < 180; searched += 1) {
      if (cursor.day() !== 0 && cursor.day() !== 6) {
        const proposedDates = [];
        const spanCursor = cursor.clone();

        while (proposedDates.length < estimatedDays) {
          if (spanCursor.day() !== 0 && spanCursor.day() !== 6) {
            proposedDates.push(spanCursor.format("YYYY-MM-DD"));
          }
          spanCursor.add(1, "day");
        }

        const ranked = candidates
          .map((user) => {
            const conflictCount = proposedDates.reduce(
              (count, dateKey) =>
                count +
                (assignedByDate.get(dateKey)?.has(user.displayName) ? 1 : 0),
              0
            );

            return { ...user, conflictCount };
          })
          .filter((user) => user.conflictCount === 0)
          .sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );

        if (ranked.length >= laborNeeded) {
          return {
            startDate: proposedDates[0],
            endDate: proposedDates[proposedDates.length - 1],
            techAssign: ranked
              .slice(0, laborNeeded)
              .map((user) => user.displayName),
            requiredSkills,
            reason: skillMatches.length
              ? "Matched description/service skills and open schedule"
              : "No exact skill match; recommended available staff",
          };
        }
      }

      cursor.add(1, "day");
    }

    return null;
  };

  const exportSelectedJobsToExcel = () => {
    const selectedJobs = filteredJobs.filter((job) =>
      selectedExportJobIds.includes(getJobExportKey(job))
    );

    if (!selectedJobs.length) {
      alert("No jobs selected for export.");
      return;
    }

    const includeRecommendations = window.confirm(
      "Would you like Excel to recommend a schedule for past-due jobs and jobs with no start date?\n\nClick OK for recommendations, or Cancel for a regular export."
    );

    const workingJobs = [...expandedHomeJobs];
    const recommendationMap = new Map();

    if (includeRecommendations) {
      selectedJobs.forEach((job) => {
        const recommendation = buildHomeExportRecommendation(
          job,
          workingJobs
        );

        if (!recommendation) return;

        recommendationMap.set(
          getJobExportKey(job),
          recommendation
        );

        workingJobs.push({
          ...job,
          startDate: recommendation.startDate,
          endDate: recommendation.endDate,
          techAssign: recommendation.techAssign,
        });
      });
    }

    const rows = selectedJobs.map((job) => {
      const recommendation = recommendationMap.get(
        getJobExportKey(job)
      );

      return {
        JobID: job.id || "",
        WorkOrder: job.wo || "",
        ClientWorkOrder: job.clientWorkOrder || "",
        SiteName: job.siteName || "",
        Address: job.address || "",
        City: job.city || "",
        State: job.state || "",
        StartDate: job.startDate || "",
        EndDate: job.endDate || "",
        OccurrenceDate: job.occurrenceDate || "",
        StartTime:
          job.startTimeMode === "custom"
            ? `Custom (${job.startTime || ""})`
            : job.startTime || "",
        EndTime: job.endTime || "",
        ServiceType: job.serviceType || "",
        Scope: job.scope || "",
        JobDescription: job.jobDescription || "",
        ClientPhone: job.clientPhone || "",
        ClientEmail: job.clientEmail || "",
        TechAssign: Array.isArray(job.techAssign)
          ? job.techAssign.join(", ")
          : job.techAssign || "",
        Status: job.status || "",
        EstimatedDays: job.jobPlan?.estimatedDays || "",
        EstimatedLabor: job.jobPlan?.estimatedLabor || "",
        Materials: (getMaterialsForJob(job) || [])
              .map(
                (m) =>
                  `${m.qty || ""} ${m.unit || ""} ${m.name || ""}${
                    m.notes ? ` (${m.notes})` : ""
                  }`
              )
              .join(" | "),
        JobTotal: getExportJobTotal(job),
        RecommendedStartDate: recommendation?.startDate || "",
        RecommendedEndDate: recommendation?.endDate || "",
        RecommendedTechAssign:
          recommendation?.techAssign?.join(", ") || "",
        RecommendedSkills:
          recommendation?.requiredSkills?.join(", ") || "",
        RecommendationReason: recommendation?.reason || "",
        Notes: Array.isArray(job.notes)
          ? job.notes
              .map(
                (n) => `${n.author || ""}: ${n.text || ""}`
              )
              .join(" | ")
          : "",
        CreatedOn: job.creationDate
          ? new Date(
              job.creationDate.seconds
                ? job.creationDate.seconds * 1000
                : job.creationDate
            ).toLocaleString()
          : "",
        CreatedBy: job.createdBy
          ? `${job.createdBy.firstName || ""} ${
              job.createdBy.lastName || ""
            }`.trim()
          : "",
      };
    });

    const totalAmount = selectedJobs.reduce(
      (sum, job) => sum + getExportJobTotal(job),
      0
    );

    rows.push({
      JobID: "",
      WorkOrder: "",
      ClientWorkOrder: "",
      SiteName: "TOTAL",
      Address: "",
      City: "",
      State: "",
      StartDate: "",
      EndDate: "",
      OccurrenceDate: "",
      StartTime: "",
      EndTime: "",
      ServiceType: "",
      Scope: "",
      JobDescription: "",
      ClientPhone: "",
      ClientEmail: "",
      TechAssign: "",
      Status: "",
      EstimatedDays: "",
      EstimatedLabor: "",
      Materials: "",
      JobTotal: totalAmount,
      RecommendedStartDate: "",
      RecommendedEndDate: "",
      RecommendedTechAssign: "",
      RecommendedSkills: "",
      RecommendationReason: "",
      Notes: "",
      CreatedOn: "",
      CreatedBy: "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.min(55, Math.max(12, key.length + 2)),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jobs");

    XLSX.writeFile(
      wb,
      `${
        includeRecommendations
          ? "Jobs_Recommended_Schedule"
          : "Jobs_Export"
      }_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`
    );
  };

  const selectAllFilteredJobsForExport = () => {
    const keys = filteredJobs.map((job) => getJobExportKey(job));
    setSelectedExportJobIds(keys);
  };
  
  const clearSelectedJobsForExport = () => {
    setSelectedExportJobIds([]);
  };
  
  const clearDateFilters = () => {
    setDateFilter("");
    setSelectedStartDate(""); // optional but recommended
  };

  const handleViewedTeamMessaging = useCallback(() => {
    setTeamHasUnread(false);
    setTeamLastSeenAt(new Date());
  }, []);
  
  const tabsForUser = useMemo(() => {
    if (isTech) {
      return ["clock-calendar"];
    }
  
    if (isStaff) {
      return ["jobs", "clock", "clock-calendar"];
    }
  
    return [];
  }, [isTech, isStaff]);
  
  

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 10 }}>
  <button
    onClick={() =>
      setLanguage((prev) => (prev === "en" ? "es" : "en"))
    }
    style={{
      padding: "6px 12px",
      borderRadius: 8,
      border: "1px solid #1d4ed8",
      background: "#e0e7ff",
      cursor: "pointer",
      fontWeight: 600,
    }}
  >
    {language === "en" ? "Español" : "English"}
  </button>
</div>
{/* ✅ STAFF-ONLY TOP NAV (Admin / Manager / Assistant) */}
{/* ✅ STAFF-ONLY TOP NAV (Admin / Manager / Assistant) */}
{isStaff && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      gap: 10,
      marginBottom: 14,
      flexWrap: "wrap",
    }}
  >
    {/* ✅ ORDER: Home → Team Dashboard → Admin Portal */}

    <button
      onClick={() => navigate("/")}
      style={topNavBtnStyle("/")}
    >
      Home
    </button>

    <button
      onClick={() => navigate("/JobHistory")}
      style={topNavBtnStyle("/JobHistory")}
    >
      Team Dashboard
    </button>

    <button
      onClick={handleAdminPortalClick}
      style={topNavBtnStyle("/SupervisorDashboard")}
    >
      Admin Portal
    </button>
  </div>
)}




<h1 className="center-text">{t("welcome")}</h1>
<p className="center-text">
  {t("role")}: {userRole}
</p>

<div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 14,
  }}
>
  <label
    htmlFor="avatar-upload"
    style={{
      cursor: "pointer",
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
    }}
  >
    {currentUserData?.avatarURL ? (
      <img
        src={currentUserData.avatarURL}
        alt="Profile Avatar"
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid #1d4ed8",
        }}
      />
    ) : (
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "#dbeafe",
          border: "2px solid #93c5fd",
        }}
      />
    )}

    <span
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: "#1d4ed8",
      }}
    >
      {uploadingAvatar ? "Uploading..." : "Click to change avatar"}
    </span>
  </label>

  <input
    id="avatar-upload"
    type="file"
    accept="image/*"
    onChange={handleAvatarUpload}
    style={{ display: "none" }}
  />
</div>

      <div style={{ marginBottom: 10, fontWeight: 600 }}>
      ⏱ {t("todaysHours")}: {clock.getTodaysHours()}
</div>

    {/* ================= TECH HOME ================= */}
{isTech && (
  <>
    {/* CLOCK — always visible */}
    <ClockPanel clock={clock} techLocation={techLocation}t={t} />

    {/* JOB CHECK-IN — current stop + running timer */}
    <ActiveJobBanner jobVisits={jobVisits} isClockedIn={clock.isClockedIn} />

    {/* TECH TAB HEADER */}
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 0,
  }}
>
  {/* LEFT: existing tabs */}
  <div style={{ display: "flex", gap: 8 }}>
  {/* Jobs */}
  <button
    onClick={() => setActiveTab("jobs")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "jobs" ? "1px solid white" : "1px solid #1d4ed8",
      background: activeTab === "jobs" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "jobs" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("jobs")}
  </button>

  {/* Job Calendar */}
  <button
    onClick={() => setActiveTab("job-calendar")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "job-calendar"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background: activeTab === "job-calendar" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "job-calendar" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("jobCalendar")}
  </button>

  {/* Clock */}
  <button
    onClick={() => setActiveTab("clock")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "clock"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background: activeTab === "clock" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "clock" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    Clock In / Out
  </button>

  {/* Time Log Calendar */}
  <button
    onClick={() => setActiveTab("time-log-calendar")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "time-log-calendar"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background:
        activeTab === "time-log-calendar" ? "#1d4ed8" : "#93c5fd",
      color:
        activeTab === "time-log-calendar" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("timeLogCalendar")}
  </button>

  {/* Rough Estimate Builder */}
  <button
    onClick={() => setActiveTab("rough-estimate-builder")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "rough-estimate-builder"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background:
        activeTab === "rough-estimate-builder" ? "#1d4ed8" : "#93c5fd",
      color:
        activeTab === "rough-estimate-builder" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    Rough Estimate Builder
  </button>
</div>

  <button
  onClick={() => setShowMoreMenu(true)}
  style={{
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    fontWeight: 700,
    cursor: "pointer",
    color: "#000",
  }}
>
  ⋯ More
</button>

  
</div>


    {/* TECH TAB CONTENT */}
    {activeTab === "job-calendar" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <EmbeddedCalendar
  jobs={techVisibleJobs}
  userRole={userRole}
  onlyTechName={isTech ? userFullName : ""}
  jobVisits={jobVisits}
  canCompleteJob={canCompleteJob}
  onBackToJobs={() => setActiveTab("jobs")}
  onJobUpdated={(updatedJob, result = null) => {
    if (updatedJob?.__deleted) {
      setJobs((previousJobs) =>
        previousJobs.filter((job) => job.id !== updatedJob.id)
      );
      setFilteredJobs((previousJobs) =>
        previousJobs.filter((job) => job.id !== updatedJob.id)
      );
      return;
    }

    if (result?.mode === "future" && result.oldJob && result.newJob) {
      setJobs((previousJobs) => [
        ...previousJobs.map((job) =>
          job.id === result.oldJob.id ? result.oldJob : job
        ),
        result.newJob,
      ]);
      return;
    }

    setJobs((previousJobs) =>
      previousJobs.map((job) =>
        job.id === updatedJob.id
          ? { ...job, ...updatedJob }
          : job
      )
    );

    setFilteredJobs((previousJobs) =>
      previousJobs.map((job) =>
        job.id === updatedJob.id
          ? { ...job, ...updatedJob }
          : job
      )
    );
  }}
  onAddSupervisorNote={addSupervisorNote}
  onContinueJob={(job) => {
    setSelectedJob(job);
    setContinueDate("");
    setShowContinuePopup(true);
  }}
  onAddTotal={openTotalPopup}
  onAddReceipt={(job) => {
    setSelectedJob(job);
    setShowReceiptPopup(true);
  }}
  onChecklist={(job) =>
    navigate(`/JobChecklist/${job.id}`, {
      state: {
        returnTo: `${location.pathname}${location.search || ""}`,
        reopenJobId: job.id,
        reopenOccurrenceDate: job.occurrenceDate || job.startDate || "",
      },
    })
  }
/>

  </div>
)}
{activeTab === "time-log-calendar" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <ClockCalendar clock={clock} jobs={techVisibleJobs} />
  </div>
)}

{activeTab === "clock-time" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <ClockTime />
  </div>
)}



{activeTab === "rough-estimate-builder" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <RoughEstimateBuilder embedded />
  </div>
)}

{activeTab === "customer-codes" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <CustomerCodes />
  </div>
)}

{activeTab === "maps" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <Maps />
  </div>
)}

{activeTab === "time-off" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <TimeOff />
  </div>
)}

{activeTab === "jobs" && (
  <>
    <h2 style={{ marginTop: 20 }}>
      {techJobsTitle}
    </h2>
    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
  techDateView: {techDateView} · myJobs: {myJobs.length} · showing: {techUpcomingJobs.length}
</div>

    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <button
        style={chip("#93c5fd", techDateView === "today")}
        onClick={() => setTechDateView("today")}
      >
        {t("today")}
      </button>

      <button
        style={chip("#facc15", techDateView === "tomorrow")}
        onClick={() => setTechDateView("tomorrow")}
      >
        {t("tomorrow")}
      </button>

      <button
        style={chip("#4ade80", techDateView === "week")}
        onClick={() => setTechDateView("week")}
      >
        {t("week")}
      </button>
    </div>

    {techUpcomingJobs.length === 0 ? (
      <p style={{ fontStyle: "italic" }}>{t("noJobs")}</p>
    ) : (
      techUpcomingJobs.map((job) => (
        <div
          key={job.id + "_" + (job.occurrenceDate || job.startDate || "")}
          style={{
            border: "1px solid #1d4ed8",
            borderRadius: 10,
            padding: 12,
            marginBottom: 10,
            background: "#f8fafc",
          }}
        >
       <strong>{job.siteName}</strong>

<p style={{ margin: "4px 0", fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
  WO #{job.wo || "—"}
</p>

<p style={{ margin: "4px 0", fontSize: 12, fontWeight: 800, color: "#334155" }}>
  Client WO #{job.clientWorkOrder || "—"}
</p>

<p style={{ margin: "4px 0", fontSize: 12, fontWeight: 700, color: "#1d4ed8" }}>
  {job.serviceType || "No Service Type"}
</p>
<p>{job.city}</p>
<p>{job.scope}</p>

<p style={{ margin: "4px 0", fontSize: 12, color: "#334155" }}>
  <strong>Date:</strong> {job.occurrenceDate || job.startDate || "No date"}
</p>

<p style={{ margin: "4px 0", fontSize: 12, color: "#334155" }}>
  <strong>Time Window:</strong>{" "}
  {job.startTimeMode === "custom"
    ? `Custom (${job.startTime || "N/A"})`
    : job.startTime || "ANY"}
</p>

<p style={{ margin: "4px 0", fontSize: 12, color: "#334155" }}>
  <strong>Company:</strong> {job.companyUse || "N/A"}
</p>
{getMaterialsForJob(job).length > 0 && (
  <div style={{ fontSize: 12, color: "#475569" }}>
    🧱 Materials required ({getMaterialsForJob(job).length})
  </div>
)}

          {/* Check in when you arrive, check out when you leave */}
          <JobCheckInButton job={job} jobVisits={jobVisits} />

          <div style={{ display: "flex", gap: 8 }}>
            <button className="blue-button" onClick={() => setSelectedJob(job)}>
              {t("viewJob")}
            </button>

            <button
              className="green-button"
              onClick={() => {
                setSelectedJob(job);
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      setTechLocation({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                      });
                    },
                    (err) => console.warn("Location error:", err),
                    { enableHighAccuracy: true }
                  );
                }
              }}
            >
              {t("startResume")}
            </button>
          </div>
        </div>
      ))
    )}
  </>
)}

  </>
)}
{/* ================= ADMIN / MANAGER / ASSISTANT HOME ================= */}
{isStaff && (
  <>
    {/* Step 2: Tab Header */}
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 0,
  }}
>
  {/* LEFT: existing tabs */}
  <div style={{ display: "flex", gap: 8 }}>
    {["jobs", "job-calendar", "clock", "time-log-calendar", "rough-estimate-builder"].map((tKey) => (
      <button
        key={tKey}
        onClick={() => setActiveTab(tKey)}
        style={{
          padding: "10px 14px",
          borderRadius: "10px 10px 0 0",
          border: "1px solid #1d4ed8",
          borderBottom:
            activeTab === tKey ? "1px solid white" : "1px solid #1d4ed8",
          background: activeTab === tKey ? "#1d4ed8" : "#93c5fd",
          color: activeTab === tKey ? "white" : "#0f172a",
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {tKey === "jobs" && "Jobs"}
        {tKey === "job-calendar" && "Job Calendar"}
        {tKey === "clock" && "Clock In / Out"}
{tKey === "time-log-calendar" && "Time Log Calendar"}
        {tKey === "rough-estimate-builder" && "Rough Estimate Builder"}

      </button>
    ))}
  </div>
  <button
  onClick={() => setShowMoreMenu(true)}
  style={{
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    fontWeight: 700,
    cursor: "pointer",
    color: "#000",
  }}
>
  ⋯ More
</button>

</div>


    {/* Step 3: Container Wrapper */}
    <div
      style={{
        border: "1px solid #1d4ed8",
        borderRadius: "0 10px 10px 10px",
        padding: 16,
        background: "#fff",
      }}
    >
    {isStaff && activeTab === "rough-estimate-builder" && (
      <RoughEstimateBuilder embedded />
    )}

    {isStaff && activeTab === "jobs" && (
  <>
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginBottom: 10,
        flexWrap: "wrap",
      }}
    >
      <button
  type="button"
  onClick={() => {
    const nextOpen = !showTeamMessaging;
    setShowTeamMessaging(nextOpen);
  
    if (nextOpen) {
      handleViewedTeamMessaging();
    }
  }}
  style={getTeamMessagingButtonStyle()}
>
<span>
  {showTeamMessaging ? "Hide Direct / Group Message" : "Show Direct / Group Message"}
  {teamUnreadLabels.length > 0
    ? ` (${teamUnreadLabels.slice(0, 2).join(", ")}${teamUnreadLabels.length > 2 ? "..." : ""})`
    : ""}
</span>

  {teamHasUnread && (
    <span
      style={{
        minWidth: 10,
        height: 10,
        borderRadius: "50%",
        background: "#dc2626",
        display: "inline-block",
      }}
      title="New team activity"
    />
  )}
</button>

      <button
        type="button"
        onClick={() => setShowMaterialsNeeded((p) => !p)}
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          border: "1px solid #1d4ed8",
          background: showMaterialsNeeded ? "#fee2e2" : "#e0e7ff",
          color: "#0f172a",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        {showMaterialsNeeded
          ? "Hide Materials Needed for Jobs"
          : "Show Materials Needed for Jobs"}
      </button>
    </div>

    <TeamMessaging
  userId={userId}
  userFullName={userFullName}
  isOpen={showTeamMessaging}
  onViewed={handleViewedTeamMessaging}
  openTaskCount={teamOpenTaskCount}
  usersList={usersList}
/>

    {showMaterialsNeeded && (
      <div
        style={{
          border: "1px solid #1d4ed8",
          borderRadius: 10,
          padding: 12,
          marginBottom: 14,
          background: "#f8fafc",
        }}
      >

   <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
  <h3 style={{ marginBottom: 6 }}>🧱 Materials Needed for Jobs</h3>

  <select
    value={materialsWeeksAhead}
    onChange={(e) => setMaterialsWeeksAhead(Number(e.target.value))}
    style={{
      padding: "6px 10px",
      borderRadius: 10,
      border: "1px solid rgba(0,0,0,0.2)",
      fontWeight: 800,
    }}
  >
    <option value={1}>This Week</option>
    <option value={4}>Next 4 Weeks</option>
  </select>
</div>


    {weeklyMaterials.length === 0 ? (
      <p style={{ fontStyle: "italic", color: "#64748b" }}>
        No materials required this week.
      </p>
    ) : (
      <ul style={{ paddingLeft: 0, marginTop: 8, listStyle: "none" }}>
      {weeklyMaterials.map((m, i) => {
        const key = `${m.jobId}_${m.materialIndex}`;
        const ordered = !!m.ordered;
        const received = !!m.received;
    
        return (
          <li
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "8px 10px",
              border: "1px solid rgba(29,78,216,0.25)",
              borderRadius: 10,
              marginBottom: 8,
              background: received ? "#dcfce7" : ordered ? "#fef9c3" : "#fff",
            }}
          >
            {/* LEFT: Material info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800 }}>
                {m.siteName} <span style={{ fontWeight: 700, color: "#334155" }}>(WO #{m.wo || "—"})</span>
              </div>
    
              <div style={{ color: "#0f172a" }}>
                {m.qty} {m.unit} {m.name}
                {m.notes && <span style={{ color: "#475569" }}> — {m.notes}</span>}
              </div>
    
              {/* Status line */}
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                {received
                  ? `✅ Received${m.receivedLocation ? ` (${m.receivedLocation})` : ""}`
                  : ordered
                  ? "🟨 Ordered"
                  : "⬜ Not ordered"}
              </div>
            </div>
    
            {/* RIGHT: Buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* ORDERED toggle */}
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  await updateMaterialStatus(m.jobId, m.materialIndex, {
                    ordered: !ordered,
                    orderedAt: !ordered ? new Date().toISOString() : null,
                    // if un-ordering, also clear received
                    ...(ordered ? { received: false, receivedAt: null, receivedLocation: "" } : {}),
                  });
                  setOpenReceivedKey(null);
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "1px solid #1d4ed8",
                  background: ordered ? "#facc15" : "#e0e7ff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Ordered
              </button>
    
              {/* RECEIVED button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // open dropdown next to this row
                  setOpenReceivedKey((prev) => (prev === key ? null : key));
                  // default draft selection
                  setReceivedLocationDraft((prev) => ({
                    ...prev,
                    [key]: prev[key] || m.receivedLocation || "Shop 1",
                  }));
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "1px solid #16a34a",
                  background: received ? "#86efac" : "#dcfce7",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Received
              </button>
    
              {/* Dropdown appears next to button */}
              {openReceivedKey === key && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <select
                    value={receivedLocationDraft[key] || "Shop 1"}
                    onChange={(e) =>
                      setReceivedLocationDraft((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                    style={{
                      padding: "6px 8px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.2)",
                      fontWeight: 700,
                    }}
                  >
                    {LOCATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
    
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const loc = receivedLocationDraft[key] || "Shop 1";
    
                      await updateMaterialStatus(m.jobId, m.materialIndex, {
                        ordered: true, // receiving implies ordered
                        orderedAt: m.orderedAt || new Date().toISOString(),
                        received: true,
                        receivedAt: new Date().toISOString(),
                        receivedLocation: loc,
                      });
    
                      setOpenReceivedKey(null);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: "1px solid #16a34a",
                      background: "#16a34a",
                      color: "white",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
    
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenReceivedKey(null);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: "1px solid #dc2626",
                      background: "#fee2e2",
                      color: "#991b1b",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    X
                  </button>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
    
    )}
       </div>
    )}
  </>
)}


      {/* ================= TAB CONTENT ================= */}
      {activeTab === "jobs" && (
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
  <div
    style={chip("#ffebee", dateFilter === "overdue")}
    onClick={() => setDateFilter("overdue")}
  >
    🚨 Overdue: {summary.overdue}
  </div>
  <div
  style={chip("#e0f2fe", dateFilter === "today")}
  onClick={() => setDateFilter("today")}
>
  📍 Due Today: {summary.dueToday}
</div>

  <div
    style={chip("#fff3cd", dateFilter === "tomorrow")}
    onClick={() => setDateFilter("tomorrow")}
  >
    📅 Due Tomorrow: {summary.dueTomorrow}
  </div>

  <div
    style={chip("#e3f2fd", dateFilter === "week")}
    onClick={() => setDateFilter("week")}
  >
    🗓 Due This Week: {summary.dueThisWeek}
  </div>

  <div
    style={chip("#dbeafe", dateFilter === "nextWeek")}
    onClick={() => setDateFilter("nextWeek")}
  >
    📆 Next Week: {summary.dueNextWeek}
  </div>

  <div
  style={chip("#f3e5f5", dateFilter === "needsScheduling")}
  onClick={() => setDateFilter("needsScheduling")}
>
  🧩 Needs Scheduling: {summary.needsScheduling}
</div>

<div
  style={chip("#e0e7ff", dateFilter === "allActive")}
  onClick={() => {
    setDateFilter("allActive");
    setSelectedStartDate(""); // ✅ clears date picker
  }}
>
  📋 All Jobs: {summary.allActive}
</div>



  <button
  onClick={() => {
    setDateFilter("");
    setSelectedStartDate("");
    setSelectedTech("ALL_TECHS");
  }}
  style={{
    padding: "6px 12px",
    borderRadius: 16,
    border: "1px solid #dc2626",
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  ✖ Clear Filters
</button>

</div>
      )}


      {activeTab === "jobs" && (
        <>
      {isAdmin && (
  <div
    style={{
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 12,
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={selectAllFilteredJobsForExport}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #1d4ed8",
        background: "#e0e7ff",
        color: "#1d4ed8",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      Select All Filtered
    </button>

    <button
      type="button"
      onClick={clearSelectedJobsForExport}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #dc2626",
        background: "#fee2e2",
        color: "#991b1b",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      Clear Selected
    </button>

    <button
      type="button"
      onClick={exportSelectedJobsToExcel}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #16a34a",
        background: "#dcfce7",
        color: "#166534",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      Export Selected to Excel
    </button>

    <span style={{ fontWeight: 700 }}>
      Selected: {selectedExportJobIds.length}
    </span>
  </div>
)}
  {/* 🔍 Search Bar */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
  <input
    type="text"
    placeholder="Search by Site, City, State, or Scope…"
    value={searchTerm}
    onChange={handleSearchChange}
    style={{
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid #1d4ed8",
      minWidth: 280,
    }}
  />

<select
  value={selectedTech}
  onChange={(e) => {
    const value = e.target.value;

    // ✅ set tech first
    setSelectedTech(value);

    // ✅ if picking a specific tech, clear anything that could "clash"
    if (value !== "ALL_TECHS") {
      setDateFilter("allActive");     // or "" if you prefer no chip active
      setSelectedStartDate("");
      setSelectedSite("ALL_SITES");
      setSortOption("");
      setSearchTerm("");              // optional, but matches your "clear other filters" goal
    }
  }}
>

    <option value="ALL_TECHS">All Technicians</option>
    {usersList
    .filter(u =>
      ["Tech", "Manager", "Assistant", "Admin"].includes(u.role)
    )
    .map(u => (
      <option
        key={u.id}
        value={`${u.firstName} ${u.lastName}`}
      >
        {u.firstName} {u.lastName} ({u.role})
      </option>
    ))}
</select>
</div>


{filteredJobs.length === 0 ? (
  <p>No jobs match the selected filters.</p>
) : (
  filteredJobs.map((job) => {
    const completed = isJobCompleted(job);
  
    return (
      <div
        key={`${job.id}_${job.occurrenceDate || toDateKey(job.startDate)}`}
        style={{
          ...jobRowStyle(job),
          position: "relative",
          paddingLeft: isAdmin ? 34 : 10,
        }}
        onClick={() => setSelectedJob(job)}
      >
        {isAdmin && (
          <input
            type="checkbox"
            checked={selectedExportJobIds.includes(getJobExportKey(job))}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleExportJob(getJobExportKey(job))}
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              margin: 0,
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
        )}
    
        <div>
          <strong>{job.siteName}</strong>
    
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>
  WO #{job.wo || "—"}
</div>

<div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginTop: 2 }}>
  Client WO #{job.clientWorkOrder || "—"}
</div>

<div style={{ fontSize: 12, fontWeight: 700, color: "#1d4ed8", marginTop: 2 }}>
  {job.serviceType || "No Service Type"}
</div>
    
          {(() => {
            const mismatch = getDurationMismatch(job);
            return mismatch && (
              <div style={{ color: "#b45309", fontSize: 12, fontWeight: 700 }}>
                ⚠️ Duration mismatch: scheduled {mismatch.scheduledDays}d / planned {mismatch.estimatedDays}d
              </div>
            );
          })()}
    
          {job.scope && (
            <span style={{ marginLeft: 6, color: "#475569", fontStyle: "italic" }}>
              ({job.scope})
            </span>
          )}{" "}
          — {job.city}
    
          <br />
    
          <small>
  {job.startDate
    ? job.endDate && job.endDate !== job.startDate
      ? `${job.startDate} → ${job.endDate}`
      : job.startDate
    : "No date"}
</small>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Time Window:</strong>{" "}
  {job.startTimeMode === "custom"
    ? `Custom (${job.startTime || "N/A"})`
    : job.startTime || "ANY"}
</div>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Company:</strong> {job.companyUse || "N/A"}
</div>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Tech:</strong>{" "}
  {Array.isArray(job.techAssign)
    ? job.techAssign.join(", ")
    : job.techAssign || "Unassigned"}
</div>
        </div>
      </div>
    );
  })
  
)}


        </>
      )}

      {activeTab === "clock" && (
        <ActiveJobBanner jobVisits={jobVisits} isClockedIn={clock.isClockedIn} />
      )}
      {activeTab === "clock" && <ClockPanel clock={clock} t={t} />}

      {!isTech && activeTab === "job-calendar" && (
        <EmbeddedCalendar
          jobs={jobs}
          userRole={userRole}
          jobVisits={jobVisits}
          canCompleteJob={canCompleteJob}
          onBackToJobs={() => setActiveTab("jobs")}
          onJobUpdated={(updatedJob) => {
            if (!updatedJob?.id) return;
            if (updatedJob.__deleted) {
              setJobs((prev) => prev.filter((job) => job.id !== updatedJob.id));
              return;
            }
            setJobs((prev) => {
              const exists = prev.some((job) => job.id === updatedJob.id);
              return exists
                ? prev.map((job) => job.id === updatedJob.id ? { ...job, ...updatedJob } : job)
                : [...prev, updatedJob];
            });
          }}
          onAddSupervisorNote={addSupervisorNote}
          onContinueJob={(job) => {
            setSelectedJob(job);
            setContinueDate("");
            setShowContinuePopup(true);
          }}
          onAddTotal={openTotalPopup}
          onAddReceipt={(job) => {
            setSelectedJob(job);
            setShowReceiptPopup(true);
          }}
          onChecklist={(job) =>
            navigate(`/JobChecklist/${job.id}`, {
              state: {
                returnTo: `${location.pathname}${location.search || ""}`,
                reopenJobId: job.id,
                reopenOccurrenceDate: job.occurrenceDate || job.startDate || "",
              },
            })
          }
        />
      )}

{activeTab === "time-log-calendar" && (
  <ClockCalendar clock={clock} jobs={jobs} />
)}     
{activeTab === "customer-codes" && <CustomerCodes />}

{activeTab === "maps" && <Maps />}

{activeTab === "time-off" && <TimeOff />}

{activeTab === "clock-time" && <ClockTime />}


    
    </div>
  </>
)}


      {selectedJob && createPortal(
        <>
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
              width: "min(1000px, calc(100vw - 40px))",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              padding: 20,
              borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
              textAlign: "left",
              boxSizing: "border-box",
              zIndex: 999999,
            }}
          >

            {!isEditing && ["Admin", "Manager", "Assistant"].includes(userRole) && (
              <QuickJobFieldEditor
                job={selectedJob}
                usersList={usersList}
                onSave={saveQuickJobFields}
              />
            )}

            {isEditing ? (
              <form className="form-container" onSubmit={handleSaveChanges}>
                <label>Site Name:</label>
                <input
                  type="text"
                  name="siteName"
                  value={editedJob.siteName || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      siteName: e.target.value,
                    }))
                  }
                />

<label style={{ display: "flex", gap: 8, alignItems: "center" }}>
  <input
    type="checkbox"
    checked={editedJob.isProject === true}
    onChange={(e) =>
      setEditedJob((prev) => ({
        ...prev,
        isProject: e.target.checked,
        projectStatus: e.target.checked
          ? prev.projectStatus || "Active"
          : "",
        projectRootId: e.target.checked
          ? prev.projectRootId || prev.id || selectedJob.id
          : "",
        projectDayNumber: e.target.checked
          ? Number(prev.projectDayNumber || 1)
          : "",
      }))
    }
  />
  Project Job
</label>

{editedJob.isProject && (
  <div
    style={{
      padding: 10,
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      borderRadius: 8,
      marginBottom: 10,
      fontWeight: 700,
    }}
  >
    Project is active. Daily completion will create the next day automatically.
  </div>
)}

<label>Client Work Order #:</label>
<input
  type="text"
  name="clientWorkOrder"
  value={editedJob.clientWorkOrder || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientWorkOrder: e.target.value,
    }))
  }
/>

                <label>Address:</label>
                <input
                  type="text"
                  name="address"
                  value={editedJob.address || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />

                <label>City:</label>
                <input
                  type="text"
                  name="city"
                  value={editedJob.city || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                />

                <label>State:</label>
                <input
                  type="text"
                  name="state"
                  value={editedJob.state || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      state: e.target.value,
                    }))
                  }
                />

                <label>Start Date:</label>
                <input
                  type="date"
                  name="startDate"
                  value={editedJob.startDate || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />

                <label>End Date:</label>
                <input
                  type="date"
                  name="endDate"
                  value={editedJob.endDate || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
                <h4 style={{ marginTop: 16 }}>Job Planning</h4>

<label>Estimated Days</label>
<input
  type="number"
  min="1"
  value={editedJob.jobPlan?.estimatedDays || ""}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedDays: Number(e.target.value),
      },
    }))
  }
/>

<label>Estimated Labor (people)</label>
<input
  type="number"
  min="1"
  value={editedJob.jobPlan?.estimatedLabor || ""}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedLabor: Number(e.target.value),
      },
    }))
  }
/>

                <label>Recurrence:</label>
<select
  value={editedJob.recurrence?.type || "none"}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        type: e.target.value,
      },
    }))
  }
>
  <option value="none">One-Time</option>
  <option value="daily">Daily</option>
  <option value="weekly">Weekly</option>
  <option value="biweekly">Bi-Weekly</option>
  <option value="monthly">Monthly</option>
  <option value="bimonthly">Bi-Monthly</option>
  <option value="quarterly">Quarterly</option>
  <option value="biyearly">Bi-Yearly</option>
  <option value="yearly">Yearly</option>
  <option value="customDates">Custom Dates</option>
</select>

{editedJob.recurrence?.type === "weekly" && (
  <>
    <label>Days of Week:</label>
    {[0,1,2,3,4,5,6].map(d => (
      <label key={d} style={{ marginRight: 8 }}>
        <input
          type="checkbox"
          checked={editedJob.recurrence?.daysOfWeek?.includes(d)}
          onChange={(e) => {
            const days = editedJob.recurrence.daysOfWeek || [];
            const updated = e.target.checked
              ? [...days, d]
              : days.filter(x => x !== d);

            setEditedJob(prev => ({
              ...prev,
              recurrence: {
                ...prev.recurrence,
                daysOfWeek: updated,
              },
            }));
          }}
        />
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]}
      </label>
    ))}
  </>
)}

{editedJob.recurrence?.type === "customDates" && (
  <>
    <label>Custom Dates (comma separated):</label>
    <input
      type="text"
      value={(editedJob.recurrence.customDates || []).join(",")}
      onChange={(e) =>
        setEditedJob(prev => ({
          ...prev,
          recurrence: {
            ...prev.recurrence,
            customDates: e.target.value
              .split(",")
              .map(d => d.trim())
              .filter(Boolean),
          },
        }))
      }
    />
  </>
)}


<label>Start Time:</label>
<select
  name="startTime"
  value={editedJob.startTime ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value !== "Custom Time") {
      setEditedJob((prev) => ({
        ...prev,
        startTime: value,
        startTimeMode: "preset",
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        startTime: "",
        startTimeMode: "custom",
      }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((opt) => (
    <option key={opt} value={opt}>{opt}</option>
  ))}
</select>

{/* Show only when Custom Time is selected */}
{editedJob.startTimeMode === "custom" && (
  <input
    type="time"
    value={editedJob.startTime || ""}
    onChange={(e) =>
      setEditedJob((prev) => ({ ...prev, startTime: e.target.value }))
    }
  />
)}

<label>End Time:</label>
<input
  type="time"
  name="endTime"
  value={editedJob.endTime || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({ ...prev, endTime: e.target.value }))
  }
/>

<label>Service Type:</label>
<select
  name="serviceType"
  value={editedJob.serviceType || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      serviceType: e.target.value,
    }))
  }
>
  <option value="">Select Service Type</option>
  {serviceTypes.map((t) => (
    <option key={t} value={t}>
      {t}
    </option>
  ))}
</select>
<label>Select Company:</label>
<select
  name="companyUse"
  value={
    ["JMC", "DCS", "OGR", "MHI", "Other"].includes(editedJob.companyUseMode)
      ? editedJob.companyUseMode
      : (
          ["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse)
            ? editedJob.companyUse
            : (editedJob.companyUse ? "Other" : "")
        )
  }
  onChange={(e) => {
    const value = e.target.value;

    if (value === "Other") {
      setEditedJob((prev) => ({
        ...prev,
        companyUseMode: "Other",
        companyUse:
          ["JMC", "DCS", "OGR", "MHI"].includes(prev.companyUse)
            ? ""
            : (prev.companyUse || ""),
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        companyUseMode: value,
        companyUse: value,
      }));
    }
  }}
>
  <option value="">Select Company</option>
  <option value="JMC">JMC</option>
  <option value="DCS">DCS</option>
  <option value="OGR">OGR</option>
  <option value="MHI">MHI</option>
  <option value="Other">Other</option>
</select>

{editedJob.companyUseMode === "Other" && (
  <>
    <label>Other Company Name:</label>
    <input
      type="text"
      value={
        ["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse)
          ? ""
          : (editedJob.companyUse || "")
      }
      onChange={(e) =>
        setEditedJob((prev) => ({
          ...prev,
          companyUse: e.target.value,
          companyUseMode: "Other",
        }))
      }
      placeholder="Enter company name"
    />
  </>
)}

                <label>Scope:</label>
                <input
                  type="text"
                  name="scope"
                  value={editedJob.scope || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      scope: e.target.value,
                    }))
                  }
                />

<label>Job Description:</label>
<textarea
  name="jobDescription"
  value={editedJob.jobDescription || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      jobDescription: e.target.value,
    }))
  }
  rows={8}
  style={{ resize: "vertical" }}
/>

<label style={{ marginTop: 10 }}>Job Description Preview:</label>
<div
  style={{
    marginTop: 8,
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    background: "#f9fafb",
    whiteSpace: "normal",
  }}
>
  {parseFormattedJobDescription(editedJob.jobDescription)}
</div>

<label>Client Phone:</label>
<input
  type="text"
  name="clientPhone"
  value={editedJob.clientPhone || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientPhone: e.target.value,
    }))
  }
/>

<label>Client Email:</label>
<input
  type="email"
  name="clientEmail"
  value={editedJob.clientEmail || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientEmail: e.target.value,
    }))
  }
/>


<label>Tech Assign:</label>

{(editedJob.techAssign || []).map((tech, index) => (
  <div
    key={index}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    }}
  >
    <select
      value={tech}
      onChange={(e) => {
        const updated = [...(editedJob.techAssign || [])];
        updated[index] = e.target.value;
        setEditedJob((prev) => ({
          ...prev,
          techAssign: updated,
        }));
      }}
      style={{ flex: 1 }}
    >
      <option value="">Select Technician</option>
      {usersList.map((u) => (
        <option key={u.id} value={`${u.firstName} ${u.lastName}`}>
          {u.firstName} {u.lastName}
        </option>
      ))}
    </select>

    <button
      type="button"
      className="red-button"
      onClick={() =>
        setEditedJob((prev) => ({
          ...prev,
          techAssign: (prev.techAssign || []).filter(
            (_, i) => i !== index
          ),
        }))
      }
      style={{
        width: "44px",
        minWidth: "44px",
        maxWidth: "44px",
        height: "44px",
        minHeight: "44px",
        padding: 0,
        fontSize: "18px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      title="Remove technician"
    >
      X
    </button>
  </div>
))}

<button
  type="button"
  className="blue-button"
  onClick={() =>
    setEditedJob((prev) => ({
      ...prev,
      techAssign: [...(prev.techAssign || []), ""],
    }))
  }
>
  Add Technician
</button>



<label>Status:</label>
<select
  name="status"
  value={editedJob.status || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      status: e.target.value,
    }))
  }
>
  <option value="">Select Status</option>
  {statusOptions.map((s) => (
    <option key={s} value={s}>
      {s}
    </option>
  ))}
</select>


                <button
                  type="button"
                  className="blue-button"
                  onClick={handleSaveChanges}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="red-button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {/* Job check-in: techs always; anyone else when assigned to this job */}
                {(isTech || isAssignedToMe(selectedJob)) && (
                  <JobCheckInButton job={selectedJob} jobVisits={jobVisits} />
                )}
                {/* Staff: who checked in here, when, and for how long */}
                {isStaff && <JobVisitHistory job={selectedJob} />}
                <p>
                  <strong>Site Name:</strong> {selectedJob.siteName}
                </p>
                <p>
  <strong>Work Order:</strong> {selectedJob.wo}
</p>
<p>
  <strong>Company:</strong> {selectedJob.companyUse || "N/A"}
</p>
<p>
  <strong>Client Work Order #:</strong>{" "}
  {selectedJob.clientWorkOrder || ""}
</p>
                <p>
                  <strong>Address:</strong> {selectedJob.address}
                </p>
                <p>
                  <strong>City:</strong> {selectedJob.city}
                </p>
                <p>
                  <strong>State:</strong> {selectedJob.state}
                </p>
                <p>
                  <strong>Start Date:</strong> {selectedJob.startDate}
                </p>
                <p>
                  <strong>End Date:</strong> {selectedJob.endDate}
                </p>
                {(selectedJob.jobPlan?.estimatedDays ||
  selectedJob.jobPlan?.estimatedLabor) && (
  <p>
    <strong>Planned:</strong>{" "}
    {selectedJob.jobPlan?.estimatedDays
      ? `${selectedJob.jobPlan.estimatedDays} day(s)`
      : "—"}
    {" · "}
    {selectedJob.jobPlan?.estimatedLabor
      ? `${selectedJob.jobPlan.estimatedLabor} people`
      : "—"}
  </p>
)}

                {getDurationMismatch(selectedJob) && (
  <p style={{ color: "#b45309", fontWeight: 700 }}>
    ⚠️ Scheduled days do not match estimated job duration
  </p>
)}

                <p>
  <strong>Time Window:</strong>{" "}
  {selectedJob.startTimeMode === "custom"
    ? `Custom (${selectedJob.startTime})`
    : selectedJob.startTime || "ANY"}
</p>

<p><strong>End Time:</strong> {selectedJob.endTime || "N/A"}</p>

<p>
  <strong>Recurrence:</strong>{" "}
  {selectedJob.recurrence?.type
    ? selectedJob.recurrence.type
    : "One-Time"}
</p>

{selectedJob.recurrence?.type === "weekly" && (
  <p>
    <strong>Days:</strong>{" "}
    {(selectedJob.recurrence.daysOfWeek || [])
      .map(d => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d])
      .join(", ")}
  </p>
)}

{selectedJob.recurrence?.type === "customDates" && (
  <p>
    <strong>Custom Dates:</strong>{" "}
    {(selectedJob.recurrence.customDates || []).join(", ")}
  </p>
)}


                <p>
                  <strong>Service Type:</strong> {selectedJob.serviceType}
                </p>
                <p>
                  <strong>Scope:</strong> {selectedJob.scope}
                </p>
                <div>
  <strong>Job Description:</strong>
  <div
    style={{
      marginTop: 8,
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#f9fafb",
      whiteSpace: "normal",
    }}
  >
    {parseFormattedJobDescription(selectedJob.jobDescription)}
  </div>
</div>
                {/* ===== Materials ===== */}
<h3 style={{ marginTop: 16 }}>Materials</h3>

{getMaterialsForJob(selectedJob).length > 0 ? (
  <div style={{ display: "grid", gap: 8 }}>
    {getMaterialsForJob(selectedJob).map((material, index) => {
      const estimatedCost =
        Number(material.qty || 0) * Number(material.costPer || 0);

      return (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
            padding: 10,
            border: "1px solid #d1d5db",
            borderRadius: 10,
            background: material.purchased ? "#ecfdf5" : "#f8fafc",
          }}
        >
          <button
            type="button"
            title={material.purchased ? "Mark as not purchased" : "Mark as purchased"}
            aria-label={material.purchased ? "Mark material as not purchased" : "Mark material as purchased"}
            onClick={() =>
              updateMaterialStatus(selectedJob.id, index, {
                purchased: !material.purchased,
                purchasedAt: !material.purchased
                  ? new Date().toISOString()
                  : "",
                purchasedBy: !material.purchased
                  ? userFullName || "User"
                  : "",
              })
            }
            className={`material-toggle${material.purchased ? " material-toggle--checked" : ""}`}
          >
            <svg
              className="material-toggle__check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 12 9 17 20 6" />
            </svg>
          </button>

          <div>
            <div style={{ fontWeight: 800 }}>
              {material.name || material.product || "Unnamed material"}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 3 }}>
              Qty: {material.qty || 0}
              {material.costPer !== "" && material.costPer !== undefined
                ? ` • Cost Per: ${Number(material.costPer || 0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}`
                : ""}
              {material.costPer !== "" && material.costPer !== undefined
                ? ` • Estimated: ${estimatedCost.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}`
                : ""}
            </div>
            {material.purchaseLocation && (
              <div style={{ fontSize: 13, marginTop: 3 }}>
                <strong>Purchase Location:</strong> {material.purchaseLocation}
              </div>
            )}
            {material.notes && (
              <div style={{ fontSize: 13, marginTop: 3 }}>
                <strong>Material Notes:</strong> {material.notes}
              </div>
            )}
            <div
              style={{
                fontSize: 12,
                marginTop: 5,
                fontWeight: 800,
                color: material.purchased ? "#166534" : "#64748b",
              }}
            >
              {material.purchased ? "Purchased" : "Not Purchased"}
            </div>

            {Array.isArray(material.files) && material.files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {material.files.map((file, fileIndex) => (
                  <a
                    key={fileIndex}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.name}
                  >
                    {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          background: "#fff",
                          fontSize: 10,
                          textAlign: "center",
                          padding: 4,
                          overflow: "hidden",
                        }}
                      >
                        {file.name}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}

    <div
      style={{
        textAlign: "right",
        fontWeight: 900,
        paddingTop: 4,
      }}
    >
      Total Estimated Material Cost:{" "}
      {getMaterialsForJob(selectedJob)
        .reduce(
          (sum, material) =>
            sum +
            Number(material.qty || 0) * Number(material.costPer || 0),
          0
        )
        .toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}
    </div>
  </div>
) : (
  <p style={{ fontStyle: "italic" }}>No materials listed.</p>
)}

                <p>
  <strong>Client Phone:</strong> {selectedJob.clientPhone || "N/A"}
</p>
<p>
  <strong>Client Email:</strong> {selectedJob.clientEmail || "N/A"}
</p>
<p>
  <strong>Tech Assign:</strong>{" "}
  {Array.isArray(selectedJob.techAssign)
    ? selectedJob.techAssign.join(", ")
    : selectedJob.techAssign || "Unassigned"}
</p>
                <p>
                  <strong>Status:</strong> {selectedJob.status}
                </p>

                {selectedJob.isProject && (
  <p
    style={{
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      padding: 8,
      borderRadius: 8,
      fontWeight: 800,
    }}
  >
    🏗️ Project Job — Day {selectedJob.projectDayNumber || 1} —{" "}
    {selectedJob.projectStatus || "Active"}
  </p>
)}

                <p>
  <strong>Created On:</strong>{" "}
  {selectedJob.creationDate
    ? new Date(
        selectedJob.creationDate.seconds
          ? selectedJob.creationDate.seconds * 1000
          : selectedJob.creationDate
      ).toLocaleString()
    : "N/A"}
</p>

<p>
  <strong>Created By:</strong>{" "}
  {selectedJob.createdBy
    ? `${selectedJob.createdBy.firstName} ${selectedJob.createdBy.lastName}`
    : "Unknown"}
</p>

<h3 style={{ marginTop: 16 }}>Receipts</h3>

<p>
  <strong>Receipt Total:</strong> {getReceiptsTotal(selectedJob).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  })}
</p>

{Array.isArray(selectedJob.receipts) && selectedJob.receipts.length > 0 ? (
  <ul>
    {selectedJob.receipts.map((receipt, index) => (
      <li key={index} style={{ marginBottom: 8 }}>
        <strong>
          {Number(receipt.amount || 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </strong>

        {receipt.note && <> — {receipt.note}</>}

        {receipt.uploadedBy && (
          <div style={{ fontSize: 12, color: "#475569" }}>
            Uploaded by {receipt.uploadedBy}
          </div>
        )}

        {receipt.fileURL && (
          <div>
            <a
              href={receipt.fileURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 700, color: "#1d4ed8" }}
            >
              View Receipt Image
            </a>
          </div>
        )}
      </li>
    ))}
  </ul>
) : (
  <p style={{ fontStyle: "italic" }}>No receipts added.</p>
)}


                <h3>Notes</h3>
                <div className="notes-container">
                {getJobNotesForDisplay(selectedJob).length > 0 ? (
  <ul>
    {getJobNotesForDisplay(selectedJob).map((note, index) => (
      <li key={index}>
        <strong>
          {note.author} ({note.timestamp}):
        </strong>{" "}
        {note.text}
        {note.files &&
          note.files.map((file, fileIndex) => (
            <p key={fileIndex}>
              <a
                href={file.fileURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 700, color: "#1d4ed8" }}
              >
                {file.fileName || `View Attachment ${fileIndex + 1}`}
              </a>
            </p>
          ))}
      </li>
    ))}
  </ul>
) : (
  <p>No notes yet for this occurrence.</p>
)}
                </div>

                {canSeeSupervisorNotes && (
  <div
    style={{
      marginTop: 16,
      padding: 12,
      border: "2px solid #7c3aed",
      borderRadius: 10,
      background: "#f5f3ff",
    }}
  >
    <h3 style={{ marginTop: 0 }}>Supervisor Notes</h3>

    {getSupervisorNotesForDisplay(selectedJob).length > 0 ? (
      <ul style={{ paddingLeft: 18 }}>
        {getSupervisorNotesForDisplay(selectedJob).map((note) => (
          <li
            key={note.id}
            style={{
              marginBottom: 10,
              padding: 8,
              background: "white",
              border: "1px solid #c4b5fd",
              borderRadius: 8,
            }}
          >
           <div>
  <div style={{ whiteSpace: "pre-wrap" }}>{note.text}</div>

  <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
    Added by {note.author || "User"}
    {note.authorRole ? ` (${note.authorRole})` : ""}{" "}
    {note.createdAt
      ? `on ${new Date(note.createdAt).toLocaleString()}`
      : ""}
  </div>
</div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No supervisor notes yet.</p>
    )}
  </div>
)}

                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
  <div>
    <h3>Uploaded Files</h3>
    <div className="uploaded-files">
      {selectedJob.fileURLs.map((fileURL, index) => (
        <div key={index}>
          <a
            href={fileURL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700, color: "#1d4ed8" }}
          >
            {selectedJob.fileNames?.[index] || `View File ${index + 1}`}
          </a>
          <p>
            {selectedJob.fileNames && selectedJob.fileNames[index]}
          </p>
        </div>
      ))}
    </div>
  </div>
)}

{selectedJob.newFiles && selectedJob.newFiles.length > 0 && (
  <div>
    <h3>New Attachments</h3>
    <div className="new-attachments">
      {selectedFiles.map((file, index) => (
        <div key={index} style={{ marginBottom: 6 }}>
          <span style={{ fontWeight: 700, color: "#1d4ed8" }}>
            {file.name}
          </span>
        </div>
      ))}
    </div>
  </div>
)}

<CollapsibleActionSection title="Supervisor" tone="supervisor" defaultOpen={false}>
  {["Admin", "Manager", "Assistant"].includes(userRole) && (
    <button
      className="grey-button"
      onClick={() => {
        setEditedJob({
          ...selectedJob,
        
          // ✅ ENSURE jobPlan ALWAYS EXISTS
          jobPlan: {
            estimatedDays: selectedJob.jobPlan?.estimatedDays || "",
            estimatedLabor: selectedJob.jobPlan?.estimatedLabor || "",
          },
        
          techAssign: Array.isArray(selectedJob.techAssign)
            ? selectedJob.techAssign
            : [selectedJob.techAssign || ""],
        
          startTimeMode: selectedJob.startTime?.includes(":")
            ? "custom"
            : "preset",
        
          companyUseMode:
            ["JMC", "DCS", "OGR", "MHI"].includes(selectedJob.companyUse)
              ? selectedJob.companyUse
              : (selectedJob.companyUse ? "Other" : ""),
        });
        

        setIsEditing(true);
      }}
    >
      Edit
    </button>
  )}

  {canSeeSupervisorNotes && (
  <button className="grey-button" onClick={addSupervisorNote}>
    Add Supervisor Note
  </button>
)}

  {!isRecurringJob(selectedJob) && selectedJob.status !== "Completed" && (
  <button
    className="yellow-button"
    onClick={() => {
      setContinueDate("");
      setShowContinuePopup(true);
    }}
  >
    Continue
  </button>
)}

  <button
    className={
      jobTotals[String(selectedJob.wo ?? selectedJob.id)]
        ? "yellow-button"
        : "green-button"
    }
    onClick={() => openTotalPopup(selectedJob)}
  >
    {jobTotals[String(selectedJob.wo ?? selectedJob.id)]
      ? "Change Total"
      : "Add Total"}
  </button>

  {/* 🔒 CLOSE JOB — Admin + Manager ONLY */}
  {canDelete && (
    <button
      className="black-button"
      onClick={handleDeleteJob}
    >
      Close Job
    </button>
  )}
</CollapsibleActionSection>

<div className="modal-buttons">
  <button
    className="blue-button"
    onClick={() => setShowNotePopup(true)}
  >
    Add Note
  </button>

  <button
    className="green-button"
    onClick={() => {
      // Techs must check in at a job before they can complete it.
      if (!canCompleteJob(selectedJob)) return;
      // Completing a job opens the Complete Job & Create Invoice form.
      setShowInvoiceModal(true);
    }}
  >
    Mark Job as Completed
  </button>

  {selectedJob.isProject && ["Admin", "Manager", "Assistant"].includes(userRole) && (
  <button
    className="red-button"
    onClick={markProjectFullyCompleted}
  >
    Project Completed
  </button>
)}
</div>

<div className="modal-buttons">

<button
  className="blue-button"
  onClick={() => setShowReceiptPopup(true)}
>
  Add Receipt
</button>

<button className="blue-button" onClick={() => setShowMaterialsPopup(true)}>
  Add / Edit Materials
</button>

<button className="grey-button" onClick={() => setShowHistoryPopup(true)}>
  History
</button>

<button
              className="yellow-button"
              onClick={() =>
                navigate(`/JobChecklist/${selectedJob.id}`, {
                  state: {
                    returnTo: `${location.pathname}${location.search || ""}`,
                    reopenJobId: selectedJob.id,
                    reopenOccurrenceDate: selectedJob.occurrenceDate || selectedJob.startDate || "",
                  },
                })
              }
            >
              📝 Checklist
            </button>

  <button className="red-button" onClick={closeModal}>
    Close
  </button>
</div>

                 
              </>
            )}
          </div>
        </>,
        document.body
      )}

{showHistoryPopup && (
  <JobHistoryPopup
    job={selectedJob}
    jobs={jobs}
    onClose={() => setShowHistoryPopup(false)}
  />
)}

{showInvoiceModal && selectedJob && (
  <JobCompletionInvoiceModal
    job={selectedJob}
    onClose={() => {
      setShowInvoiceModal(false);
      if (completionSubmittedRef.current) {
        completionSubmittedRef.current = false;
        setSelectedJob(null);
        // Full refresh so Calendar / SendInvoices / Job History pick the
        // completion up. activeTab is persisted, so we land on the same tab.
        refreshKeepingView();
      }
    }}
    onCompleted={async (invoice) => {
      completionSubmittedRef.current = true;
      const recurring = isRecurringJob(selectedJob);
      const occurrenceKey = getCompletionOccurrenceKey(selectedJob);
      // Leaving the job: close the open check-in for it.
      await jobVisits.checkOutIfAt(selectedJob, "job_completed");

      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id
            ? {
                ...j,
                status: recurring ? j.status : "Completed",
                completedDates: recurring
                  ? [...(j.completedDates || []), occurrenceKey]
                  : j.completedDates,
                approvedInvoiceId: invoice.id,
                invoiceStatus: invoice.status,
              }
            : j
        )
      );

      setSelectedJob((prev) =>
        prev
          ? {
              ...prev,
              status: recurring ? prev.status : "Completed",
              approvedInvoiceId: invoice.id,
              invoiceStatus: invoice.status,
            }
          : prev
      );
    }}
  />
)}

{showMaterialsPopup && createPortal(
  <>
    <div
      onClick={() => setShowMaterialsPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "min(1050px, 96vw)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3 style={{ marginBottom: 4 }}>Add / Edit Materials</h3>
      <p style={{ marginTop: 0, color: "#475569", fontSize: 13 }}>
        Check Purchased when the material has been bought. Estimated Material
        Cost is calculated automatically from Qty × Cost Per.
      </p>

      <div style={{ display: "grid", gap: 12 }}>
        {materialsDraft.map((material, index) => {
          const estimatedCost = getEstimatedMaterialCost(material);

          return (
            <div
              key={index}
              style={{
                border: material.purchased
                  ? "2px solid #22c55e"
                  : "1px solid #cbd5e1",
                borderRadius: 12,
                padding: 12,
                background: material.purchased ? "#ecfdf5" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(material.purchased)}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        purchased: event.target.checked,
                        purchasedAt: event.target.checked
                          ? new Date().toISOString()
                          : "",
                        purchasedBy: event.target.checked
                          ? userFullName || "User"
                          : "",
                      })
                    }
                    style={{ width: 20, height: 20 }}
                  />
                  Purchased
                </label>

                <button
                  type="button"
                  className="red-button"
                  onClick={() => removeMaterialDraft(index)}
                  style={{ width: "auto" }}
                >
                  Remove
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 10,
                }}
              >
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Product
                  <input
                    type="text"
                    placeholder="Example: 2x4 pressure-treated lumber"
                    value={material.name || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, { name: event.target.value })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Qty
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={material.qty ?? ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        qty:
                          event.target.value === ""
                            ? ""
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Cost Per
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={material.costPer ?? ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        costPer:
                          event.target.value === ""
                            ? ""
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Estimated Material Cost
                  <input
                    type="text"
                    readOnly
                    value={estimatedCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                    style={{
                      background: "#e2e8f0",
                      fontWeight: 900,
                      cursor: "not-allowed",
                    }}
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Purchase Location
                  <input
                    type="text"
                    placeholder="Example: Home Depot - Leesburg"
                    value={material.purchaseLocation || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        purchaseLocation: event.target.value,
                      })
                    }
                  />
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 5,
                    fontWeight: 800,
                    gridColumn: "1 / -1",
                  }}
                >
                  Material Notes
                  <textarea
                    rows="3"
                    placeholder="Color, size, model number, pickup instructions, or other notes"
                    value={material.notes || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, { notes: event.target.value })
                    }
                  />
                </label>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  Photos / Files
                </div>

                {Array.isArray(material.files) && material.files.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {material.files.map((file, fileIndex) => (
                      <div key={fileIndex} style={{ position: "relative" }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                          {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #cbd5e1",
                                borderRadius: 6,
                                background: "#fff",
                                fontSize: 10,
                                textAlign: "center",
                                padding: 4,
                                overflow: "hidden",
                              }}
                            >
                              {file.name}
                            </div>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMaterialFile(index, fileIndex)}
                          title="Remove file"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "1px solid #b91c1c",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="blue-button"
        onClick={addMaterial}
        style={{ marginTop: 12 }}
      >
        + Add Material
      </button>

      <div
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 10,
          background: "#eff6ff",
          border: "1px solid #93c5fd",
          textAlign: "right",
          fontWeight: 900,
          fontSize: 16,
        }}
      >
        Total Estimated Material Cost:{" "}
        {materialsDraft
          .reduce(
            (sum, material) => sum + getEstimatedMaterialCost(material),
            0
          )
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
      </div>

      <div className="modal-buttons">
        <button className="green-button" onClick={saveMaterials}>
          Save Materials
        </button>
        <button
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

{showReceiptPopup && createPortal(
  <>
    <div
      onClick={() => setShowReceiptPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>

    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h2>Add Receipt</h2>

      <label>Receipt Amount</label>
      <input
        type="number"
        value={receiptAmount}
        onChange={(e) => setReceiptAmount(e.target.value)}
        placeholder="Enter receipt amount"
      />

      <label>Receipt Note</label>
      <input
        type="text"
        value={receiptNote}
        onChange={(e) => setReceiptNote(e.target.value)}
        placeholder="Example: Home Depot materials"
      />

      <label>Upload Receipt Image</label>
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
      />

      {receiptFile && (
        <p style={{ fontSize: 12, color: "#475569" }}>
          Selected: {receiptFile.name}
        </p>
      )}

      <div className="modal-buttons">
        <button
          className="green-button"
          onClick={saveReceipt}
          disabled={savingReceipt}
        >
          {savingReceipt ? "Saving..." : "Save Receipt"}
        </button>

        <button
          className="red-button"
          onClick={() => setShowReceiptPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

      {showNotePopup && createPortal(
        <>
          <div
            onClick={closeNotePopup}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Add Note</h2>
            <textarea
              className="form-container"
              rows="4"
              placeholder="Enter your note here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            ></textarea>
            <input
              type="file"
              accept="image/*, .pdf"
              onChange={handleFileChange}
              multiple
            />
            <div className="modal-buttons">
              <button
                className="green-button"
                onClick={saveNote}
                disabled={uploading}
              >
                {uploading ? <LoadingSpinner label="Saving note..." /> : "Save"}
              </button>
              <button className="red-button" onClick={closeNotePopup}>
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

{showContinuePopup && selectedJob && createPortal(
  <>
    <div
      onClick={() => setShowContinuePopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>

    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3>Continue Job</h3>

      <p>
        This will complete the current job for{" "}
        <strong>{toDateKey(selectedJob.startDate) || "the current date"}</strong>{" "}
        and create a new active job copy.
      </p>

      <label>New Continue Date:</label>
      <input
        type="date"
        value={continueDate}
        onChange={(e) => setContinueDate(e.target.value)}
      />

      {continueDate && (
        <p style={{ color: "#b45309", fontWeight: 700 }}>
          Warning: You are about to complete this job for today and schedule a
          new job for {continueDate}. Do you want to continue?
        </p>
      )}

      <div className="button-group">
        <button
          className="shortgreen-button"
          onClick={continueJobToNewDate}
          disabled={continuingJob || !continueDate}
        >
          {continuingJob ? "Saving..." : "Yes"}
        </button>

        <button
          className="shortred-button"
          onClick={() => {
            setShowContinuePopup(false);
            setContinueDate("");
          }}
          disabled={continuingJob}
        >
          No
        </button>
      </div>
    </div>
  </>,
  document.body
)}

      {showVerificationPopup && createPortal(
        <>
          <div
            onClick={closeVerificationPopup}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h3>Are you sure you want to complete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={markJobCompleted}>
                Yes
              </button>
              <button
                className="shortred-button"
                onClick={closeVerificationPopup}
              >
                No
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

{showMoreMenu && (
  <>
    {/* backdrop */}
<div
  className="modal-overlay"
  onClick={() => setShowMoreMenu(false)}
  style={{
    position: "fixed",
    inset: 0,
    zIndex: 999,
  }}
></div>


    {/* menu */}
    <div
      style={{
        position: "fixed",
        top: "20%",
        left: "50%",
        transform: "translateX(-50%)",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        padding: 12,
        zIndex: 1001,
        minWidth: 240,
      }}
    >
      {rightNavTabs
  .filter((x) => !(isTech && x.tab === "clock-time"))
  .map((x) => (

  <button
    key={x.path}
    onClick={() => {
      setShowMoreMenu(false);
      setActiveTab(x.tab);
    }}
    
    style={{
      width: "100%",
      padding: "12px 14px",
      marginBottom: 8,

      borderRadius: 10,
      border: "1px solid #1d4ed8",
      background: "#e0e7ff",
      color: "#0f172a",

      fontWeight: 700,
      cursor: "pointer",
      textAlign: "left",
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = "#c7d2fe")
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.background = "#e0e7ff")
    }
  >
    {x.label}
  </button>
))}


      <div style={{ textAlign: "right", marginTop: 8 }}>
        <button
          onClick={() => setShowMoreMenu(false)}
          className="red-button"
        >
          Close
        </button>
      </div>
    </div>
  </>
)}

{showTotalPopup && selectedJob && createPortal(
  <>
    <div
      onClick={() => setShowTotalPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3>{jobTotals[String(selectedJob.wo ?? selectedJob.id)]
 ? "Change Total" : "Add Total"}</h3>

      <label>Total Amount ($)</label>
      <input
        type="number"
        value={totalInput}
        onChange={(e) => setTotalInput(e.target.value)}
        placeholder="Enter amount"
      />

      <div className="modal-buttons">
        <button className="green-button" onClick={saveTotal}>
          Save
        </button>
        <button className="red-button" onClick={() => setShowTotalPopup(false)}>
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

    </div>
  );
};

export default Home;